import React, { useState, useRef } from 'react';
import api from '../services/api';
import { toast } from '../components/Toast';
import { WifiOff, XCircle, Download, Package, Users, FileText, Banknote, Settings, ClipboardList, Building, Lightbulb, Upload, FolderOpen, CheckCircle2, AlertTriangle, CreditCard, Zap } from 'lucide-react';

const BusinessOffline = () => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmData, setConfirmData] = useState(null); // données parsées en attente de confirmation
  const fileInputRef = useRef();

  // ==========================================
  // GENERATION DU FICHIER HTML HORS LIGNE
  // ==========================================
  const handleGenerateOffline = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/business/offline-export');
      const { data } = res.data;
      
      const htmlContent = generateOfflineHTML(data);
      
      // Télécharger le fichier
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workspace-hors-ligne-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la génération du fichier hors ligne');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // IMPORT DES DONNEES HORS LIGNE
  // ==========================================
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setError(null);
    setImportResult(null);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Vérifier que c'est bien un fichier d'export offline valide
      if (!data.exported_from_offline) {
        setError('Ce fichier ne semble pas provenir du mode hors ligne.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      // Afficher le dialog de confirmation avec le résumé
      setConfirmData(data);
    } catch (err) {
      console.error(err);
      if (err instanceof SyntaxError) {
        setError('Fichier JSON invalide. Assurez-vous d\'utiliser le fichier exporté depuis le mode hors ligne.');
      } else {
        setError('Erreur lors de la lecture du fichier');
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!confirmData) return;
    
    setImporting(true);
    setConfirmData(null);
    
    try {
      const res = await api.post('/business/offline-import', confirmData);
      setImportResult(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setConfirmData(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <WifiOff size={18} /> Mode Hors Ligne
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Travaillez sans connexion internet puis synchronisez vos données
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p className="font-medium"><XCircle size={16} className="inline mr-1" />{error}</p>
        </div>
      )}

      {/* Section Export */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Download size={18} /> Générer le fichier hors ligne
          </h2>
          <p className="text-indigo-100 text-sm mt-1">
            Un fichier HTML autonome sera téléchargé avec toutes vos données actuelles
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {[
              { icon: <Package size={24} />, label: 'Produits & Prix', desc: 'Recherche et consultation' },
              { icon: <Users size={24} />, label: 'Clients', desc: 'Liste complète' },
              { icon: <FileText size={24} />, label: 'Factures brouillon', desc: 'Création et impression' },
              { icon: <Banknote size={24} />, label: 'Dépenses', desc: 'Enregistrement' },
              { icon: <Settings size={24} />, label: 'Prestations', desc: 'Suivi des services' },
              { icon: <ClipboardList size={24} />, label: 'Inventaire', desc: 'Continuer un comptage' },
              { icon: <Building size={24} />, label: 'Info entreprise', desc: 'Pour les factures' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                <span>{item.icon}</span>
                <p className="text-sm font-medium text-gray-700 mt-1">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleGenerateOffline}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Génération en cours...
              </>
            ) : (
              <><Download size={18} className="inline mr-1" /> Télécharger le workspace hors ligne</>
            )}
          </button>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-700">
              <strong><Lightbulb size={16} className="inline mr-1" /> Comment ça marche :</strong>
            </p>
            <ol className="text-sm text-amber-600 mt-2 space-y-1 list-decimal list-inside">
              <li>Cliquez sur le bouton pour télécharger le fichier HTML</li>
              <li>Ouvrez ce fichier dans n'importe quel navigateur (même sans internet)</li>
              <li>Créez des factures, enregistrez des dépenses, ajoutez des clients...</li>
              <li>Quand vous revenez en ligne, exportez les données et importez-les ici</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Section Import */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Upload size={18} /> Importer les données hors ligne
          </h2>
          <p className="text-green-100 text-sm mt-1">
            Importez le fichier JSON exporté depuis le mode hors ligne
          </p>
        </div>
        
        <div className="p-6">
          <div 
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-green-400 transition cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="block mb-3"><FolderOpen size={48} /></span>
            <p className="text-gray-600 font-medium">
              {importing ? 'Import en cours...' : 'Cliquez pour sélectionner le fichier JSON'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Format accepté: .json</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImport}
            />
          </div>

          {importing && (
            <div className="mt-4 flex items-center justify-center gap-3 text-green-600">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-600 border-t-transparent"></div>
              Import en cours...
            </div>
          )}

          {importResult && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="font-bold text-green-700 mb-3"><CheckCircle2 size={16} className="inline mr-1" />{importResult.message}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Clients', count: importResult.results?.clients?.imported, errors: importResult.results?.clients?.errors },
                  { label: 'Factures', count: importResult.results?.invoices?.imported, errors: importResult.results?.invoices?.errors },
                  { label: 'Dépenses', count: importResult.results?.expenses?.imported, errors: importResult.results?.expenses?.errors },
                  { label: 'Prestations', count: importResult.results?.prestations?.imported, errors: importResult.results?.prestations?.errors },
                  { label: 'Inventaires', count: importResult.results?.inventories?.imported, errors: importResult.results?.inventories?.errors },
                ].map((r, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{r.count || 0}</p>
                    <p className="text-xs text-gray-500">{r.label}</p>
                    {r.errors?.length > 0 && (
                      <p className="text-xs text-red-500 mt-1">{r.errors.length} erreur(s)</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation d'import */}
      {confirmData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={cancelImport}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden my-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'fadeInUp 0.25s ease-out' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white">
              <div className="flex items-center gap-3">
                <AlertTriangle size={24} />
                <div>
                  <h3 className="text-lg font-bold">Confirmer l'importation</h3>
                  <p className="text-amber-100 text-sm">Vérifiez les données avant d'importer</p>
                </div>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              {/* Info fichier */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-xs text-gray-500 mb-1">Fichier exporté le</p>
                <p className="text-sm font-medium text-gray-700">
                  {confirmData.export_date ? new Date(confirmData.export_date).toLocaleString('fr-FR', { 
                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                  }) : 'Date inconnue'}
                </p>
              </div>

              {/* Résumé des données */}
              <p className="text-sm font-semibold text-gray-700 mb-3">Données à importer :</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { icon: <Users size={20} />, label: 'Clients', count: confirmData.clients?.length || 0, color: 'indigo' },
                  { icon: <FileText size={20} />, label: 'Factures', count: confirmData.invoices?.length || 0, color: 'blue' },
                  { icon: <Banknote size={20} />, label: 'Dépenses', count: confirmData.expenses?.length || 0, color: 'red' },
                  { icon: <CreditCard size={20} />, label: 'Paiements', count: confirmData.payments?.length || 0, color: 'orange' },
                  { icon: <Settings size={20} />, label: 'Prestations', count: confirmData.prestations?.length || 0, color: 'green' },
                  { icon: <ClipboardList size={20} />, label: 'Inventaires', count: confirmData.inventories?.length || 0, color: 'purple' },
                ].filter(item => item.count > 0).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <span>{item.icon}</span>
                    <div>
                      <p className="text-lg font-bold text-gray-800">{item.count}</p>
                      <p className="text-xs text-gray-500">{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Montants */}
              {(confirmData.invoices?.length > 0 || confirmData.expenses?.length > 0) && (
                <div className="bg-indigo-50 rounded-xl p-4 mb-4 space-y-2">
                  {confirmData.invoices?.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total factures</span>
                      <span className="font-bold text-indigo-600">
                        {confirmData.invoices.reduce((s, inv) => s + (inv.total || 0), 0).toLocaleString('fr-FR')} XOF
                      </span>
                    </div>
                  )}
                  {confirmData.expenses?.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total dépenses</span>
                      <span className="font-bold text-red-600">
                        {confirmData.expenses.reduce((s, exp) => s + (exp.montant || 0), 0).toLocaleString('fr-FR')} XOF
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Avertissement */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
                <p className="text-xs text-amber-700">
                  <strong><Zap size={16} className="inline mr-1" /> Attention :</strong> Cette action va créer de nouvelles entrées dans votre workspace. 
                  Les données importées ne peuvent pas être annulées automatiquement.
                </p>
              </div>

              {/* Boutons */}
              <div className="flex gap-3">
                <button
                  onClick={cancelImport}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmImport}
                  className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={16} className="inline mr-1" /> Importer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ==========================================
// GENERATEUR HTML HORS LIGNE COMPLET
// ==========================================
function generateOfflineHTML(data) {
  const { settings, logo_base64, products, clients, prestations, inventories, exported_at } = data;
  const devise = settings?.devise || 'XOF';
  
  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Workspace Hors Ligne - ${settings?.nom_entreprise || 'Mon Entreprise'}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f3f4f6; color: #1f2937; line-height: 1.5; }
  .container { max-width: 1200px; margin: 0 auto; padding: 16px; }
  
  /* Header */
  .header { background: linear-gradient(135deg, #4f46e5, #7c3aed); color: white; padding: 20px; border-radius: 16px; margin-bottom: 20px; }
  .header h1 { font-size: 1.5em; margin-bottom: 4px; }
  .header .subtitle { opacity: 0.8; font-size: 0.85em; }
  .header .offline-badge { display: inline-block; background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 20px; font-size: 0.75em; margin-top: 8px; }
  
  /* Navigation Tabs */
  .tabs { display: flex; gap: 4px; background: white; padding: 6px; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow-x: auto; flex-wrap: nowrap; }
  .tab { padding: 10px 16px; border-radius: 8px; border: none; background: none; cursor: pointer; font-size: 0.85em; font-weight: 500; white-space: nowrap; transition: all 0.2s; color: #6b7280; }
  .tab:hover { background: #f3f4f6; }
  .tab.active { background: #4f46e5; color: white; }
  
  /* Cards */
  .card { background: white; border-radius: 12px; padding: 20px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  .card h2 { font-size: 1.1em; margin-bottom: 12px; color: #374151; }
  .card h3 { font-size: 1em; margin-bottom: 8px; color: #4b5563; }
  
  /* Forms */
  .form-group { margin-bottom: 12px; }
  .form-group label { display: block; font-size: 0.8em; font-weight: 600; color: #374151; margin-bottom: 4px; }
  .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.9em; outline: none; transition: border 0.2s; }
  .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
  textarea { resize: vertical; min-height: 60px; }
  
  /* Buttons */
  .btn { padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; font-size: 0.85em; font-weight: 600; transition: all 0.2s; display: inline-flex; align-items: center; gap: 6px; }
  .btn-primary { background: #4f46e5; color: white; }
  .btn-primary:hover { background: #4338ca; }
  .btn-success { background: #059669; color: white; }
  .btn-success:hover { background: #047857; }
  .btn-danger { background: #dc2626; color: white; }
  .btn-danger:hover { background: #b91c1c; }
  .btn-secondary { background: #f3f4f6; color: #374151; }
  .btn-secondary:hover { background: #e5e7eb; }
  .btn-sm { padding: 4px 10px; font-size: 0.8em; }
  
  /* Tables */
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; padding: 8px 12px; font-size: 0.75em; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #e5e7eb; }
  td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; font-size: 0.85em; }
  tr:hover { background: #f9fafb; }
  
  /* Search */
  .search-box { width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 0.9em; margin-bottom: 16px; outline: none; }
  .search-box:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
  
  /* Grid */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  
  /* Responsive */
  @media (max-width: 768px) {
    .container { padding: 8px; }
    
    /* Header mobile */
    .header { padding: 14px; border-radius: 12px; margin-bottom: 12px; }
    .header h1 { font-size: 1.1em; }
    
    /* Tabs: grille 4 colonnes, 2 lignes */
    .tabs { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 4px; border-radius: 10px; margin-bottom: 12px; }
    .tab { padding: 10px 4px; font-size: 0.7em; text-align: center; border-radius: 8px; display: flex; flex-direction: column; align-items: center; gap: 2px; line-height: 1.2; }
    
    /* Cards mobile */
    .card { padding: 14px; border-radius: 10px; margin-bottom: 10px; }
    .card h2 { font-size: 1em; }
    
    /* Grids: 2 colonnes sur mobile */
    .grid-2, .grid-3 { grid-template-columns: 1fr 1fr; }
    .grid-4 { grid-template-columns: 1fr 1fr; gap: 8px; }
    
    /* Stats cards mobile */
    .stat-card { padding: 10px 6px; }
    .stat-card .number { font-size: 1.15em; }
    .stat-card .label { font-size: 0.65em; }
    
    /* Table mobile: carte layout */
    .mobile-cards table, .mobile-cards thead { display: none; }
    .mobile-card-list { display: flex; flex-direction: column; gap: 8px; }
    .mobile-card-item { background: #f9fafb; border-radius: 10px; padding: 12px; border-left: 3px solid #4f46e5; }
    
    /* Product table → card view */
    #section-products table { display: none; }
    .product-cards-mobile { display: flex !important; flex-direction: column; gap: 8px; }
    .product-card-m { background: #f9fafb; border-radius: 10px; padding: 12px; border-left: 3px solid #4f46e5; }
    .product-card-m .pc-name { font-weight: 700; font-size: 0.95em; margin-bottom: 2px; }
    .product-card-m .pc-code { font-size: 0.75em; color: #6b7280; }
    .product-card-m .pc-row { display: flex; justify-content: space-between; align-items: center; margin-top: 6px; font-size: 0.85em; }
    .product-card-m .pc-stock { font-weight: 600; }
    
    /* Client table → card view */
    #clients-table-container table { display: none; }
    .client-cards-mobile { display: flex !important; flex-direction: column; gap: 8px; }
    .client-card-m { background: #f9fafb; border-radius: 10px; padding: 12px; border-left: 3px solid #7c3aed; }
    
    /* Prestations table → card view */
    #prestations-existing table { display: none; }
    .presta-cards-mobile { display: flex !important; flex-direction: column; gap: 8px; }
    .presta-card-m { background: #f9fafb; border-radius: 10px; padding: 12px; border-left: 3px solid #059669; }
    
    /* Tables overflow */
    table { font-size: 0.75em; }
    th, td { padding: 6px 6px; }
    
    /* Buttons mobile */
    .btn { padding: 10px 14px; font-size: 0.85em; }
    .btn-sm { padding: 6px 10px; font-size: 0.75em; }
    
    /* Search box mobile */
    .search-box { padding: 12px 14px; font-size: 0.95em; border-radius: 12px; }
    
    /* Invoice form mobile */
    .inv-form-grid-2 { grid-template-columns: 1fr !important; }
    .inv-form-grid-4 { grid-template-columns: 1fr 1fr !important; }
    
    /* Invoice header mobile */
    .invoice-header { flex-direction: column; gap: 12px; }
    .invoice-title { text-align: left; }
    .invoice-title h2 { font-size: 1.3em; }
    
    /* Totals mobile */
    .totals { justify-content: stretch; }
    .totals-box { width: 100%; }
    
    /* Inventory form mobile cards */
    .inv-mobile-cards { display: flex !important; flex-direction: column; gap: 8px; }
    .inv-mobile-card { background: #f9fafb; border-radius: 10px; padding: 12px; border: 1px solid #e5e7eb; }
    .inv-mobile-card .imc-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
    .inv-mobile-card .imc-name { font-weight: 700; font-size: 0.9em; }
    .inv-mobile-card .imc-code { font-size: 0.7em; color: #6b7280; }
    .inv-mobile-card .imc-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; align-items: center; }
    .inv-mobile-card .imc-label { font-size: 0.65em; color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em; }
    .inv-mobile-card .imc-val { font-weight: 600; font-size: 0.9em; }
    .inv-mobile-card .imc-input { width: 100%; padding: 10px 8px; border: 2px solid #d1d5db; border-radius: 10px; text-align: center; font-size: 1.1em; font-weight: 600; background: white; }
    .inv-mobile-card .imc-input:focus { border-color: #4f46e5; outline: none; box-shadow: 0 0 0 3px rgba(79,70,229,0.15); }
    .inv-mobile-card .imc-notes { width: 100%; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.8em; margin-top: 6px; }
    #inventory-items-table { display: none; }
    
    /* Inventory form header mobile */
    .inv-form-header { flex-direction: column; gap: 8px; }
    .inv-form-header h3 { font-size: 1em; }
    .inv-form-actions { width: 100%; display: flex; gap: 8px; }
    .inv-form-actions .btn { flex: 1; justify-content: center; }
    
    /* Alerts mobile */
    .alert { font-size: 0.8em; padding: 10px 12px; }
    
    /* Print modal mobile */
    .print-modal { padding: 16px; }
    
    /* Flex header mobile */
    .section-header-flex { flex-direction: column; gap: 8px; align-items: stretch !important; }
    .section-header-flex .btn { width: 100%; justify-content: center; }
    
    /* Export grid */
    #section-export .grid-4 { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  }
  
  @media (max-width: 380px) {
    .tabs { grid-template-columns: repeat(4, 1fr); }
    .tab { font-size: 0.65em; padding: 8px 2px; }
    .grid-4 { grid-template-columns: 1fr 1fr; }
    #section-export .grid-4 { grid-template-columns: 1fr 1fr; }
    .inv-mobile-card .imc-grid { grid-template-columns: 1fr 1fr; }
  }
  
  /* Section visibility */
  .section { display: none; }
  .section.active { display: block; }
  
  /* Badge */
  .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 0.75em; font-weight: 600; }
  .badge-blue { background: #dbeafe; color: #1d4ed8; }
  .badge-green { background: #d1fae5; color: #065f46; }
  .badge-red { background: #fee2e2; color: #991b1b; }
  .badge-amber { background: #fef3c7; color: #92400e; }
  .badge-gray { background: #f3f4f6; color: #374151; }
  
  /* Invoice print */
  .invoice-preview { border: 1px solid #e5e7eb; border-radius: 8px; padding: 24px; background: white; }
  .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e5e7eb; }
  .invoice-company { display: flex; gap: 12px; align-items: flex-start; }
  .invoice-company img { width: 60px; height: 60px; object-fit: contain; border-radius: 8px; }
  .invoice-title { text-align: right; }
  .invoice-title h2 { font-size: 1.8em; color: #4f46e5; }
  
  /* Alert boxes */
  .alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 12px; font-size: 0.85em; }
  .alert-info { background: #dbeafe; color: #1d4ed8; }
  .alert-success { background: #d1fae5; color: #065f46; }
  .alert-warning { background: #fef3c7; color: #92400e; }
  
  /* Stats */
  .stat-card { text-align: center; padding: 16px; background: #f9fafb; border-radius: 10px; }
  .stat-card .number { font-size: 1.5em; font-weight: 700; color: #4f46e5; }
  .stat-card .label { font-size: 0.75em; color: #6b7280; margin-top: 4px; }
  
  /* Print styles */
  @media print {
    body { background: white; }
    .no-print { display: none !important; }
    .invoice-preview, .inventory-print { border: none; padding: 0; }
    @page { size: A4; margin: 15mm; }
  }

  /* Print inventory modal */
  .print-modal-overlay { position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1100;padding:16px; }
  .print-modal { background:white;border-radius:16px;padding:24px;max-width:460px;width:100%; }
  
  /* Modal */
  .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
  .modal { background: white; border-radius: 16px; padding: 24px; max-width: 500px; width: 100%; max-height: 90vh; overflow-y: auto; }
  .modal h3 { margin-bottom: 16px; }
  
  /* Totals */
  .totals { display: flex; justify-content: flex-end; margin-top: 16px; }
  .totals-box { width: 280px; }
  .totals-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
  .totals-row.total { background: #eef2ff; padding: 10px; border-radius: 8px; font-weight: 700; font-size: 1.1em; border: none; margin-top: 6px; }
  
  /* Line items */
  .line-items { margin-bottom: 16px; }
  .line-item { display: grid; grid-template-columns: 1fr auto auto auto auto; gap: 8px; align-items: center; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
  .line-item-header { font-weight: 600; font-size: 0.75em; color: #6b7280; text-transform: uppercase; }
  
  /* Product select */
  .product-search-results { position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #d1d5db; border-radius: 10px; max-height: 280px; overflow-y: auto; box-shadow: 0 8px 25px rgba(0,0,0,0.15); z-index: 100; margin-top: 4px; }
  .product-search-result { padding: 8px 12px; cursor: pointer; font-size: 0.85em; transition: background 0.15s; border-bottom: 1px solid #f3f4f6; }
  .product-search-result:last-child { border-bottom: none; }
  .product-search-result:hover { background: #eef2ff; }
  .relative { position: relative; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
  .modal { background: white; padding: 24px; border-radius: 16px; width: 100%; max-width: 420px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
</style>
</head>
<body>

<div class="container">
  <!-- Header -->
  <div class="header">
    <h1>${settings?.nom_entreprise || 'Mon Entreprise'} - Mode Hors Ligne</h1>
    <div class="subtitle">Données exportées le ${new Date(exported_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
    <div class="offline-badge">Fonctionnel sans internet</div>
  </div>

  <!-- Tabs -->
  <div class="tabs no-print" id="tabs">
    <button class="tab active" onclick="showSection('products')">Produits</button>
    <button class="tab" onclick="showSection('invoices')">Factures</button>
    <button class="tab" onclick="showSection('expenses')">Dépenses</button>
    <button class="tab" onclick="showSection('payments')">Paiements</button>
    <button class="tab" onclick="showSection('clients')">Clients</button>
    <button class="tab" onclick="showSection('prestations')">Prestations</button>
    <button class="tab" onclick="showSection('inventory')">Inventaire</button>
    <button class="tab" onclick="showSection('export')">Exporter</button>
  </div>

  <!-- ==================== PRODUITS ==================== -->
  <div id="section-products" class="section active">
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Produits (${products?.length || 0})</h2>
      </div>
      <input type="text" class="search-box" placeholder="Rechercher un produit (nom, code, catégorie)..." oninput="filterProducts(this.value)">
      <div class="grid-4" style="margin-bottom:12px;">
        <div class="stat-card"><div class="number" id="stat-total-products">${products?.length || 0}</div><div class="label">Produits</div></div>
        <div class="stat-card"><div class="number" id="stat-total-stock">${products?.reduce((s, p) => s + (parseInt(p.stock_actuel) || 0), 0)}</div><div class="label">Stock total</div></div>
        <div class="stat-card"><div class="number" id="stat-valeur-stock">${products?.reduce((s, p) => s + ((parseFloat(p.prix_vente) || 0) * (parseInt(p.stock_actuel) || 0)), 0).toLocaleString('fr-FR')}</div><div class="label">Valeur stock (vente)</div></div>
        <div class="stat-card"><div class="number" id="stat-low-stock">${products?.filter(p => parseInt(p.stock_actuel) <= parseInt(p.stock_minimum)).length || 0}</div><div class="label">Stock bas</div></div>
      </div>
      <div style="overflow-x:auto;">
        <table>
          <thead>
            <tr>
              <th>Produit</th>
              <th>Catégorie</th>
              <th>Prix Achat</th>
              <th>Prix Vente</th>
              <th>Stock</th>
              <th>Marge</th>
            </tr>
          </thead>
          <tbody id="products-table">
            ${(products || []).map(p => `
            <tr class="product-row" data-search="${(p.nom + ' ' + (p.code_produit || '') + ' ' + (p.categorie || '') + ' ' + (p.description || '')).toLowerCase()}">
              <td>
                <div style="font-weight:600;">${escapeHtml(p.nom)}</div>
                ${p.code_produit ? `<div style="font-size:0.75em;color:#6b7280;">${escapeHtml(p.code_produit)}</div>` : ''}
                ${p.description ? `<div style="font-size:0.75em;color:#9ca3af;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(p.description)}</div>` : ''}
              </td>
              <td>${p.categorie ? `<span class="badge badge-blue">${escapeHtml(p.categorie)}</span>` : '-'}</td>
              <td>${parseFloat(p.prix_achat || 0).toLocaleString('fr-FR')} ${devise}</td>
              <td style="font-weight:600;">${parseFloat(p.prix_vente || 0).toLocaleString('fr-FR')} ${devise}</td>
              <td>
                <span class="badge ${parseInt(p.stock_actuel) <= parseInt(p.stock_minimum) ? 'badge-red' : 'badge-green'}">
                  ${p.stock_actuel} ${p.unite || 'unité'}
                </span>
              </td>
              <td style="color:#059669;font-weight:600;">${((parseFloat(p.prix_vente) || 0) - (parseFloat(p.prix_achat) || 0)).toLocaleString('fr-FR')} ${devise}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <!-- Mobile product cards (hidden on desktop, shown on mobile via CSS) -->
      <div class="product-cards-mobile" id="products-cards-mobile" style="display:none;">
        ${(products || []).map(p => `
        <div class="product-card-m product-row-m" data-search="${(p.nom + ' ' + (p.code_produit || '') + ' ' + (p.categorie || '') + ' ' + (p.description || '')).toLowerCase()}">
          <div class="pc-name">${escapeHtml(p.nom)}</div>
          ${p.code_produit ? `<div class="pc-code">#${escapeHtml(p.code_produit)}${p.categorie ? ' • ' + escapeHtml(p.categorie) : ''}</div>` : (p.categorie ? `<div class="pc-code">${escapeHtml(p.categorie)}</div>` : '')}
          <div class="pc-row">
            <span>Achat: ${parseFloat(p.prix_achat || 0).toLocaleString('fr-FR')} ${devise}</span>
            <span style="font-weight:700;">Vente: ${parseFloat(p.prix_vente || 0).toLocaleString('fr-FR')} ${devise}</span>
          </div>
          <div class="pc-row">
            <span class="badge ${parseInt(p.stock_actuel) <= parseInt(p.stock_minimum) ? 'badge-red' : 'badge-green'}" style="font-size:0.8em;">${p.stock_actuel} ${p.unite || 'unité'}</span>
            <span style="color:#059669;font-weight:600;font-size:0.85em;">Marge: ${((parseFloat(p.prix_vente) || 0) - (parseFloat(p.prix_achat) || 0)).toLocaleString('fr-FR')} ${devise}</span>
          </div>
        </div>`).join('')}
      </div>
      ${(!products || products.length === 0) ? '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucun produit</p>' : ''}
    </div>
  </div>

  <!-- ==================== FACTURES ==================== -->
  <div id="section-invoices" class="section">
    <div class="card no-print">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Factures brouillon</h2>
        <button class="btn btn-primary" onclick="showInvoiceForm()">+ Nouvelle facture</button>
      </div>
      <div class="alert alert-info">
        Les factures créées ici sont des <strong>brouillons</strong>. Elles ne seront confirmées que lors de l'import en ligne.
      </div>
      <div id="invoices-list"></div>
    </div>
    <div id="invoice-form-container"></div>
    <div id="invoice-preview-container"></div>
  </div>

  <!-- ==================== DEPENSES ==================== -->
  <div id="section-expenses" class="section">
    <div class="card">
      <div class="section-header-flex" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Dépenses</h2>
        <button class="btn btn-primary" onclick="showExpenseForm()">+ Nouvelle dépense</button>
      </div>
      <div id="expenses-list"></div>
      <div id="expense-form-container"></div>
    </div>
  </div>

  <!-- ==================== PAIEMENTS ==================== -->
  <div id="section-payments" class="section">
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Paiements / Décaissements</h2>
      </div>
      <div class="alert alert-info" style="margin-bottom:12px;">Les décaissements sont générés automatiquement lorsqu'une dépense est créée.</div>
      <div id="payments-list"></div>
    </div>
  </div>

  <!-- ==================== CLIENTS ==================== -->
  <div id="section-clients" class="section">
    <div class="card">
      <div class="section-header-flex" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Clients</h2>
        <button class="btn btn-primary" onclick="showClientForm()">+ Nouveau client</button>
      </div>
      <input type="text" class="search-box" placeholder="Rechercher un client..." oninput="filterClients(this.value)">
      <div id="clients-table-container">
        <table>
          <thead><tr><th>Nom</th><th>Téléphone</th><th>Email</th><th>Adresse</th></tr></thead>
          <tbody id="clients-table">
            ${(clients || []).map(c => `
            <tr class="client-row" data-search="${(c.nom + ' ' + (c.telephone || '') + ' ' + (c.email || '')).toLowerCase()}" data-id="${c.id}">
              <td style="font-weight:600;">${escapeHtml(c.nom)}</td>
              <td>${c.telephone || '-'}</td>
              <td>${c.email || '-'}</td>
              <td>${c.adresse || '-'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
        <!-- Mobile client cards -->
        <div class="client-cards-mobile" id="clients-cards-mobile" style="display:none;">
          ${(clients || []).map(c => `
          <div class="client-card-m client-row-m" data-search="${(c.nom + ' ' + (c.telephone || '') + ' ' + (c.email || '')).toLowerCase()}">
            <div style="font-weight:700;font-size:0.95em;">${escapeHtml(c.nom)}</div>
            ${c.telephone ? `<div style="font-size:0.85em;color:#6b7280;margin-top:2px;">${escapeHtml(c.telephone)}</div>` : ''}
            ${c.email ? `<div style="font-size:0.85em;color:#6b7280;">${escapeHtml(c.email)}</div>` : ''}
            ${c.adresse ? `<div style="font-size:0.8em;color:#9ca3af;margin-top:2px;">${escapeHtml(c.adresse)}</div>` : ''}
          </div>`).join('')}
        </div>
      </div>
      ${(!clients || clients.length === 0) ? '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucun client</p>' : ''}
      <div id="client-form-container"></div>
    </div>
  </div>

  <!-- ==================== PRESTATIONS ==================== -->
  <div id="section-prestations" class="section">
    <div class="card">
      <div class="section-header-flex" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Prestations</h2>
        <button class="btn btn-primary" onclick="showPrestationForm()">+ Nouvelle prestation</button>
      </div>
      <div id="prestations-existing">
        ${(prestations || []).length > 0 ? `
        <table>
          <thead><tr><th>Titre</th><th>Client</th><th>Montant</th><th>Statut</th></tr></thead>
          <tbody>
            ${prestations.map(p => `
            <tr>
              <td style="font-weight:600;">${escapeHtml(p.titre)}</td>
              <td>${p.client_nom || '-'}</td>
              <td>${parseFloat(p.montant || 0).toLocaleString('fr-FR')} ${devise}</td>
              <td><span class="badge badge-blue">${p.statut || 'en_cours'}</span></td>
            </tr>`).join('')}
          </tbody>
        </table>
        <!-- Mobile prestation cards -->
        <div class="presta-cards-mobile" style="display:none;">
          ${prestations.map(p => `
          <div class="presta-card-m">
            <div style="font-weight:700;font-size:0.95em;">${escapeHtml(p.titre)}</div>
            <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;">
              <span style="font-size:0.85em;color:#6b7280;">${p.client_nom || 'Sans client'}</span>
              <span style="font-weight:600;">${parseFloat(p.montant || 0).toLocaleString('fr-FR')} ${devise}</span>
            </div>
            <div style="margin-top:4px;"><span class="badge badge-blue">${p.statut || 'en_cours'}</span></div>
          </div>`).join('')}
        </div>` : '<p style="text-align:center;color:#9ca3af;padding:12px;">Aucune prestation existante</p>'}
      </div>
      <div id="new-prestations-list" style="margin-top:16px;"></div>
      <div id="prestation-form-container"></div>
    </div>
  </div>

  <!-- ==================== INVENTAIRE ==================== -->
  <div id="section-inventory" class="section">
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h2>Inventaire</h2>
      </div>
      <div class="alert alert-info" style="margin-bottom:12px;">
        Continuez vos inventaires en cours. Comptez le stock physique et les écarts seront calculés automatiquement.
      </div>
      <div id="inventory-list"></div>
      <div id="inventory-form-container"></div>
    </div>
  </div>

  <!-- ==================== EXPORT ==================== -->
  <div id="section-export" class="section">
    <div class="card">
      <h2>Exporter les nouvelles données</h2>
      <p style="color:#6b7280;margin-bottom:16px;font-size:0.9em;">
        Exportez toutes les données créées hors ligne en fichier JSON pour les importer dans votre workspace en ligne.
      </p>
      <div class="grid-4" style="margin-bottom:16px;">
        <div class="stat-card"><div class="number" id="export-invoices-count">0</div><div class="label">Factures</div></div>
        <div class="stat-card"><div class="number" id="export-expenses-count">0</div><div class="label">Dépenses</div></div>
        <div class="stat-card"><div class="number" id="export-payments-count">0</div><div class="label">Paiements</div></div>
        <div class="stat-card"><div class="number" id="export-clients-count">0</div><div class="label">Nvx Clients</div></div>
        <div class="stat-card"><div class="number" id="export-prestations-count">0</div><div class="label">Prestations</div></div>
        <div class="stat-card"><div class="number" id="export-inventories-count">0</div><div class="label">Inventaires</div></div>
      </div>
      <button class="btn btn-success" onclick="exportData()" style="width:100%;padding:14px;font-size:1em;">
        Télécharger le fichier d'import (.json)
      </button>
      <div class="alert alert-warning" style="margin-top:12px;">
        Pensez à exporter vos données avant de fermer cette page ! Les données non exportées seront perdues.
      </div>
    </div>
  </div>
</div>

<script>
// ==========================================
// DONNEES EMBARQUEES
// ==========================================
const SETTINGS = ${JSON.stringify(settings || {})};
const LOGO_BASE64 = ${JSON.stringify(logo_base64 || null)};
const PRODUCTS = ${JSON.stringify(products || [])};
const EXISTING_CLIENTS = ${JSON.stringify(clients || [])};
const EXISTING_PRESTATIONS = ${JSON.stringify(prestations || [])};
const EXISTING_INVENTORIES = ${JSON.stringify(inventories || [])};
const DEVISE = ${JSON.stringify(devise)};
const EXPORTED_AT = ${JSON.stringify(exported_at)};

// Données créées hors ligne
let offlineInvoices = [];
let offlineExpenses = [];
let offlinePayments = [];
let offlineClients = [];
let offlinePrestations = [];
let updatedInventories = {};
let invoiceCounter = 1;
let paymentCounter = 1;

// Tous les clients (existants + offline)
function getAllClients() {
  return [...EXISTING_CLIENTS, ...offlineClients];
}

// ==========================================
// NAVIGATION
// ==========================================
function showSection(name) {
  document.querySelectorAll('.section').forEach(s => s.classList.remove('active'));
  document.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
  document.getElementById('section-' + name).classList.add('active');
  event.target.classList.add('active');
  updateExportStats();
}

// ==========================================
// PRODUITS - RECHERCHE
// ==========================================
function filterProducts(query) {
  const q = query.toLowerCase();
  document.querySelectorAll('.product-row').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
  document.querySelectorAll('.product-row-m').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
}

function filterClients(query) {
  const q = query.toLowerCase();
  document.querySelectorAll('.client-row').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
  document.querySelectorAll('.client-row-m').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
}

// ==========================================
// FACTURES
// ==========================================
function showInvoiceForm() {
  const allClients = getAllClients();
  const container = document.getElementById('invoice-form-container');
  container.innerHTML = \`
    <div style="margin-top:16px;">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
        <h3 style="font-size:1.3em;font-weight:700;color:#1f2937;">\${editingInvoiceIdx !== null ? 'Modifier la facture' : 'Nouvelle facture'}</h3>
      </div>
      
      <div class="inv-form-grid-2" style="display:grid;grid-template-columns:2fr 1fr;gap:20px;">
        <!-- Colonne principale -->
        <div style="display:flex;flex-direction:column;gap:16px;">
          
          <!-- Client -->
          <div class="card" style="padding:20px;">
            <h3 style="font-weight:600;margin-bottom:12px;">Client</h3>
            <div style="display:flex;gap:8px;">
              <select id="inv-client" style="flex:1;padding:10px 14px;border:1px solid #d1d5db;border-radius:10px;font-size:0.9em;">
                <option value="">Sélectionner un client</option>
                \${allClients.map(c => \`<option value="\${c.id || c.offline_id}" data-is-offline="\${c.offline_id ? '1' : '0'}">\${esc(c.nom)}\${c.telephone ? ' - ' + c.telephone : ''}</option>\`).join('')}
              </select>
              <button class="btn btn-secondary" onclick="showQuickClientModal()" style="white-space:nowrap;">+ Nouveau</button>
            </div>
            <div id="inv-client-info" style="margin-top:10px;"></div>
          </div>
          
          <!-- Détails facture -->
          <div class="card" style="padding:20px;">
            <h3 style="font-weight:600;margin-bottom:12px;">Détails facture</h3>
            <div class="grid-3">
              <div class="form-group">
                <label>Type</label>
                <select id="inv-type" style="padding:10px 14px;border:1px solid #d1d5db;border-radius:10px;">
                  <option value="standard">Facture standard</option>
                  <option value="proforma">Facture proforma</option>
                </select>
              </div>
              <div class="form-group">
                <label>Date facture</label>
                <input type="date" id="inv-date" value="\${new Date().toISOString().slice(0,10)}">
              </div>
              <div class="form-group">
                <label>Date d'échéance</label>
                <input type="date" id="inv-date-echeance" value="">
              </div>
            </div>
          </div>
          
          <!-- Articles / Services -->
          <div class="card" style="padding:20px;">
            <h3 style="font-weight:600;margin-bottom:12px;">Articles / Services</h3>
            <div id="inv-lines" style="display:flex;flex-direction:column;gap:12px;"></div>
            <button onclick="addInvoiceLine()" style="margin-top:12px;width:100%;padding:10px;border:2px dashed #d1d5db;border-radius:10px;background:none;color:#6b7280;cursor:pointer;font-size:0.9em;transition:all 0.2s;" onmouseover="this.style.borderColor='#4f46e5';this.style.color='#4f46e5'" onmouseout="this.style.borderColor='#d1d5db';this.style.color='#6b7280'">
              + Ajouter une ligne
            </button>
          </div>
          
          <!-- Notes & Conditions -->
          <div class="card" style="padding:20px;">
            <h3 style="font-weight:600;margin-bottom:12px;">Notes & Conditions</h3>
            <div class="form-group">
              <label>Notes (visible sur la facture)</label>
              <textarea id="inv-notes" placeholder="Notes ou remarques..." rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Conditions de paiement</label>
              <textarea id="inv-conditions" placeholder="Ex: Paiement à 30 jours..." rows="2"></textarea>
            </div>
          </div>
        </div>
        
        <!-- Sidebar Récapitulatif -->
        <div>
          <div class="card" style="padding:20px;position:sticky;top:16px;">
            <h3 style="font-weight:600;margin-bottom:12px;">Récapitulatif</h3>
            <div style="display:flex;flex-direction:column;gap:8px;">
              <div style="display:flex;justify-content:space-between;color:#6b7280;">
                <span>Sous-total</span>
                <span id="inv-sous-total-display">0 ${devise}</span>
              </div>
              <div class="form-group" style="margin-bottom:4px;">
                <label style="font-size:0.75em;">Remise (${devise})</label>
                <input type="number" id="inv-remise" value="0" min="0" oninput="calcInvoiceTotal()" style="padding:8px 12px;">
              </div>
              <div class="form-group" style="margin-bottom:4px;">
                <label style="font-size:0.75em;">Taxe (${devise})</label>
                <input type="number" id="inv-taxe" value="0" min="0" oninput="calcInvoiceTotal()" style="padding:8px 12px;">
              </div>
              <div style="border-top:2px solid #e5e7eb;padding-top:12px;margin-top:4px;">
                <div style="display:flex;justify-content:space-between;font-size:1.2em;font-weight:700;">
                  <span>Total</span>
                  <span style="color:#4f46e5;" id="inv-total-display">0 ${devise}</span>
                </div>
              </div>
            </div>
            <div style="display:flex;flex-direction:column;gap:8px;margin-top:20px;">
              <button class="btn btn-primary" onclick="saveInvoice()" style="width:100%;padding:12px;font-size:0.95em;">Enregistrer brouillon</button>
              <button class="btn btn-secondary" onclick="cancelInvoiceForm()" style="width:100%;padding:10px;">Annuler</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal nouveau client rapide -->
    <div id="quick-client-modal" style="display:none;"></div>
  \`;
  
  // Pré-remplir si édition
  if (editingInvoiceIdx !== null) {
    const inv = offlineInvoices[editingInvoiceIdx];
    document.getElementById('inv-client').value = inv.client_id ? (inv.client_id.startsWith('offline_') ? inv.client_id.replace('offline_', '') : inv.client_id) : '';
    document.getElementById('inv-type').value = inv.type || 'standard';
    document.getElementById('inv-date').value = inv.date_facture || '';
    document.getElementById('inv-date-echeance').value = inv.date_echeance || '';
    document.getElementById('inv-remise').value = inv.remise || 0;
    document.getElementById('inv-taxe').value = inv.taxe || 0;
    document.getElementById('inv-notes').value = inv.notes || '';
    document.getElementById('inv-conditions').value = inv.conditions || '';
    // Ajouter les lignes existantes
    (inv.items || []).forEach(item => {
      addInvoiceLine(item);
    });
  } else {
    addInvoiceLine();
  }
  
  // Afficher info client sélectionné
  document.getElementById('inv-client').addEventListener('change', updateClientInfo);
  updateClientInfo();
}

let editingInvoiceIdx = null;

function cancelInvoiceForm() {
  editingInvoiceIdx = null;
  document.getElementById('invoice-form-container').innerHTML = '';
}

function updateClientInfo() {
  const select = document.getElementById('inv-client');
  const infoDiv = document.getElementById('inv-client-info');
  if (!infoDiv) return;
  const clientId = select.value;
  if (!clientId) { infoDiv.innerHTML = ''; return; }
  
  const allC = getAllClients();
  const client = allC.find(c => String(c.id) === clientId || c.offline_id === clientId);
  if (!client) { infoDiv.innerHTML = ''; return; }
  
  infoDiv.innerHTML = \`<div style="padding:10px 14px;background:#f9fafb;border-radius:10px;font-size:0.85em;color:#4b5563;">
    <div style="font-weight:600;color:#1f2937;">\${esc(client.nom)}</div>
    \${client.telephone ? '<div>' + esc(client.telephone) + '</div>' : ''}
    \${client.email ? '<div>' + esc(client.email) + '</div>' : ''}
    \${client.adresse ? '<div>' + esc(client.adresse) + '</div>' : ''}
  </div>\`;
}

function showQuickClientModal() {
  const modal = document.getElementById('quick-client-modal');
  modal.style.display = 'block';
  modal.innerHTML = \`
    <div class="modal-overlay" onclick="if(event.target===this)closeQuickClientModal()">
      <div class="modal">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <h3 style="font-weight:700;">Nouveau client</h3>
          <button onclick="closeQuickClientModal()" style="border:none;background:none;font-size:1.2em;cursor:pointer;color:#9ca3af;"></button>
        </div>
        <div class="form-group"><label>Nom *</label><input type="text" id="qcl-nom" placeholder="Nom du client"></div>
        <div class="form-group"><label>Téléphone</label><input type="text" id="qcl-telephone" placeholder="Numéro"></div>
        <div class="form-group"><label>Email</label><input type="email" id="qcl-email" placeholder="Email"></div>
        <div style="display:flex;gap:8px;margin-top:16px;">
          <button class="btn btn-secondary" onclick="closeQuickClientModal()" style="flex:1;padding:10px;">Annuler</button>
          <button class="btn btn-primary" onclick="saveQuickClient()" style="flex:1;padding:10px;">Créer</button>
        </div>
      </div>
    </div>
  \`;
}

function closeQuickClientModal() {
  const modal = document.getElementById('quick-client-modal');
  if (modal) { modal.style.display = 'none'; modal.innerHTML = ''; }
}

function saveQuickClient() {
  const nom = document.getElementById('qcl-nom')?.value;
  if (!nom) { toast.warning('Le nom est obligatoire'); return; }
  
  const client = {
    offline_id: 'offline_cl_' + Date.now(),
    nom: nom,
    telephone: document.getElementById('qcl-telephone')?.value || null,
    email: document.getElementById('qcl-email')?.value || null,
    adresse: null,
    notes: null,
    created_offline: new Date().toISOString()
  };
  offlineClients.push(client);
  
  // Ajouter au select et sélectionner
  const select = document.getElementById('inv-client');
  const opt = document.createElement('option');
  opt.value = client.offline_id;
  opt.dataset.isOffline = '1';
  opt.textContent = client.nom + (client.telephone ? ' - ' + client.telephone : '');
  select.appendChild(opt);
  select.value = client.offline_id;
  updateClientInfo();
  
  // Aussi mettre à jour la table clients
  const tbody = document.getElementById('clients-table');
  if (tbody) {
    const tr = document.createElement('tr');
    tr.className = 'client-row';
    tr.dataset.search = (client.nom + ' ' + (client.telephone || '') + ' ' + (client.email || '')).toLowerCase();
    tr.innerHTML = \`<td style="font-weight:600;">\${esc(client.nom)} <span class="badge badge-amber">Nouveau</span></td><td>\${client.telephone || '-'}</td><td>\${client.email || '-'}</td><td>-</td>\`;
    tbody.appendChild(tr);
  }
  
  closeQuickClientModal();
  updateExportStats();
}

function addInvoiceLine(prefill) {
  const container = document.getElementById('inv-lines');
  const idx = container.children.length;
  const div = document.createElement('div');
  div.style.cssText = 'padding:14px;background:#f9fafb;border-radius:10px;';
  
  const productName = prefill?.product_id ? (PRODUCTS.find(p => String(p.id) === String(prefill.product_id))?.nom || '') : '';
  
  div.innerHTML = \`
    <!-- Ligne 1: Produit + Description -->
    <div class="inv-form-grid-2" style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
      <div class="relative" style="position:relative;">
        <label style="display:block;font-size:0.75em;color:#6b7280;margin-bottom:4px;">Produit (optionnel)</label>
        <input type="text" class="inv-line-product-search" value="\${esc(productName)}" placeholder="Rechercher un produit..." oninput="searchProductLine(this, \${idx})" onfocus="searchProductLine(this, \${idx})" autocomplete="off" style="width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:8px;font-size:0.85em;">
        <div id="product-results-\${idx}" class="product-search-results" style="display:none;"></div>
        <input type="hidden" class="inv-line-product-id" value="\${prefill?.product_id || ''}">
        <button class="inv-line-clear-product" onclick="clearProductSelection(this, \${idx})" style="position:absolute;right:8px;top:28px;border:none;background:none;color:#9ca3af;cursor:pointer;font-size:0.9em;display:\${prefill?.product_id ? 'block' : 'none'};"></button>
      </div>
      <div>
        <label style="display:block;font-size:0.75em;color:#6b7280;margin-bottom:4px;">Description *</label>
        <input type="text" class="inv-line-desc" value="\${esc(prefill?.description || '')}" placeholder="Description de l'article ou service" style="width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:8px;font-size:0.85em;">
      </div>
    </div>
    <!-- Ligne 2: Qté + Prix + Supprimer + Sous-total -->
    <div class="inv-form-grid-4" style="display:grid;grid-template-columns:100px 140px auto 120px;gap:10px;align-items:end;">
      <div>
        <label style="display:block;font-size:0.75em;color:#6b7280;margin-bottom:4px;">Quantité</label>
        <input type="number" min="1" value="\${prefill?.quantite || 1}" class="inv-line-qty" oninput="calcInvoiceTotal()" style="width:100%;padding:10px;border:1px solid #d1d5db;border-radius:8px;font-size:0.85em;text-align:center;">
      </div>
      <div>
        <label style="display:block;font-size:0.75em;color:#6b7280;margin-bottom:4px;">Prix unitaire (${devise})</label>
        <input type="number" min="0" value="\${prefill?.prix_unitaire || ''}" placeholder="0" class="inv-line-price" oninput="calcInvoiceTotal()" style="width:100%;padding:10px;border:1px solid #d1d5db;border-radius:8px;font-size:0.85em;">
      </div>
      <div>
        <button onclick="removeInvoiceLine(this)" style="width:100%;padding:10px;background:#fee2e2;color:#dc2626;border:none;border-radius:8px;cursor:pointer;font-size:0.85em;">Supprimer</button>
      </div>
      <div style="text-align:right;">
        <label style="display:block;font-size:0.75em;color:#6b7280;margin-bottom:4px;">Sous-total</label>
        <div class="inv-line-total" style="font-weight:600;padding:10px 0;font-size:0.9em;">0 ${devise}</div>
      </div>
    </div>
  \`;
  container.appendChild(div);
  calcInvoiceTotal();
}

function removeInvoiceLine(btn) {
  const line = btn.closest('#inv-lines > div');
  if (document.getElementById('inv-lines').children.length <= 1) return;
  line.remove();
  // Re-index dropdowns
  reindexLines();
  calcInvoiceTotal();
}

function reindexLines() {
  document.querySelectorAll('#inv-lines > div').forEach((line, idx) => {
    const searchInput = line.querySelector('.inv-line-product-search');
    if (searchInput) {
      searchInput.setAttribute('oninput', 'searchProductLine(this, ' + idx + ')');
      searchInput.setAttribute('onfocus', 'searchProductLine(this, ' + idx + ')');
    }
    const resultsDiv = line.querySelector('.product-search-results');
    if (resultsDiv) resultsDiv.id = 'product-results-' + idx;
    const clearBtn = line.querySelector('.inv-line-clear-product');
    if (clearBtn) clearBtn.setAttribute('onclick', 'clearProductSelection(this, ' + idx + ')');
  });
}

function clearProductSelection(btn, idx) {
  const line = document.getElementById('inv-lines').children[idx];
  if (!line) return;
  line.querySelector('.inv-line-product-search').value = '';
  line.querySelector('.inv-line-product-id').value = '';
  btn.style.display = 'none';
  const resultsDiv = document.getElementById('product-results-' + idx);
  if (resultsDiv) resultsDiv.style.display = 'none';
}

function searchProductLine(input, idx) {
  const query = input.value.toLowerCase();
  const resultsDiv = document.getElementById('product-results-' + idx);
  if (!resultsDiv) return;
  
  const matches = query.length < 1
    ? PRODUCTS.slice(0, 10)
    : PRODUCTS.filter(p => 
        p.nom.toLowerCase().includes(query) || 
        (p.code_produit || '').toLowerCase().includes(query) ||
        (p.categorie || '').toLowerCase().includes(query) ||
        (p.description || '').toLowerCase().includes(query)
      ).slice(0, 10);
  
  if (matches.length === 0) {
    resultsDiv.innerHTML = '<div style="padding:10px;text-align:center;color:#9ca3af;font-size:0.85em;">Aucun produit trouvé</div>';
    resultsDiv.style.display = 'block';
    return;
  }
  
  const line = document.getElementById('inv-lines').children[idx];
  const currentProductId = line?.querySelector('.inv-line-product-id')?.value;
  
  resultsDiv.innerHTML = matches.map(p => \`
    <div class="product-search-result" onclick="selectProductLine(\${idx}, '\${p.id}')" style="display:flex;align-items:center;gap:10px;padding:8px 12px;\${String(p.id) === currentProductId ? 'background:#eef2ff;' : ''}">
      <div style="width:32px;height:32px;background:linear-gradient(135deg,#4f46e5,#7c3aed);border-radius:8px;display:flex;align-items:center;justify-content:center;color:white;font-size:0.75em;font-weight:700;flex-shrink:0;">\${(p.nom || '?').charAt(0).toUpperCase()}</div>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:600;font-size:0.85em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">\${esc(p.nom)}</div>
        <div style="font-size:0.75em;color:#6b7280;">\${p.code_produit ? '#' + esc(p.code_produit) + ' ' : ''}\${p.categorie ? '<span style="color:#4f46e5;">' + esc(p.categorie) + '</span>' : ''}</div>
      </div>
      <div style="font-weight:600;color:#059669;font-size:0.85em;white-space:nowrap;">\${parseFloat(p.prix_vente).toLocaleString('fr-FR')} ${devise}</div>
    </div>
  \`).join('');
  resultsDiv.style.display = 'block';
}

function selectProductLine(idx, productId) {
  const product = PRODUCTS.find(p => String(p.id) === String(productId));
  if (!product) return;
  
  const line = document.getElementById('inv-lines').children[idx];
  if (!line) return;
  
  line.querySelector('.inv-line-product-search').value = product.nom;
  line.querySelector('.inv-line-product-id').value = product.id;
  line.querySelector('.inv-line-desc').value = product.nom;
  line.querySelector('.inv-line-price').value = product.prix_vente || 0;
  
  const clearBtn = line.querySelector('.inv-line-clear-product');
  if (clearBtn) clearBtn.style.display = 'block';
  
  const resultsDiv = document.getElementById('product-results-' + idx);
  if (resultsDiv) resultsDiv.style.display = 'none';
  
  calcInvoiceTotal();
}

// Hide product results when clicking elsewhere
document.addEventListener('click', function(e) {
  if (!e.target.classList.contains('inv-line-product-search')) {
    document.querySelectorAll('.product-search-results').forEach(d => d.style.display = 'none');
  }
});

function calcInvoiceTotal() {
  let sousTotal = 0;
  document.querySelectorAll('#inv-lines > div').forEach(line => {
    const qty = parseFloat(line.querySelector('.inv-line-qty')?.value) || 0;
    const price = parseFloat(line.querySelector('.inv-line-price')?.value) || 0;
    const lineTotal = qty * price;
    sousTotal += lineTotal;
    const totalEl = line.querySelector('.inv-line-total');
    if (totalEl) totalEl.textContent = lineTotal.toLocaleString('fr-FR') + ' ' + DEVISE;
  });
  const remise = parseFloat(document.getElementById('inv-remise')?.value) || 0;
  const taxe = parseFloat(document.getElementById('inv-taxe')?.value) || 0;
  const total = sousTotal - remise + taxe;
  
  const stDisplay = document.getElementById('inv-sous-total-display');
  if (stDisplay) stDisplay.textContent = sousTotal.toLocaleString('fr-FR') + ' ' + DEVISE;
  const display = document.getElementById('inv-total-display');
  if (display) display.textContent = total.toLocaleString('fr-FR') + ' ' + DEVISE;
}

function saveInvoice() {
  const items = [];
  document.querySelectorAll('#inv-lines > div').forEach(line => {
    const desc = line.querySelector('.inv-line-desc')?.value;
    const qty = parseFloat(line.querySelector('.inv-line-qty')?.value) || 0;
    const price = parseFloat(line.querySelector('.inv-line-price')?.value) || 0;
    const productId = line.querySelector('.inv-line-product-id')?.value || null;
    if (desc && qty > 0) {
      items.push({ description: desc, quantite: qty, prix_unitaire: price, total: qty * price, product_id: productId });
    }
  });
  
  if (items.length === 0) { toast.warning('Ajoutez au moins une ligne de facture avec une description'); return; }
  
  const sousTotal = items.reduce((s, i) => s + i.total, 0);
  const remise = parseFloat(document.getElementById('inv-remise')?.value) || 0;
  const taxe = parseFloat(document.getElementById('inv-taxe')?.value) || 0;
  const clientSelect = document.getElementById('inv-client');
  const clientId = clientSelect.value || null;
  const clientOption = clientSelect.selectedOptions[0];
  const clientNom = clientId ? clientOption.textContent.split(' - ')[0].trim() : null;
  const isOfflineClient = clientOption?.dataset?.isOffline === '1';
  const invoiceType = document.getElementById('inv-type')?.value || 'standard';
  
  const invoice = {
    offline_id: 'inv_' + Date.now(),
    numero: 'BROUILLON-' + String(invoiceCounter++).padStart(4, '0'),
    client_id: isOfflineClient ? 'offline_' + clientId : clientId,
    client_nom: clientNom,
    type: invoiceType,
    date_facture: document.getElementById('inv-date')?.value || new Date().toISOString().slice(0, 10),
    date_echeance: document.getElementById('inv-date-echeance')?.value || null,
    sous_total: sousTotal,
    remise: remise,
    taxe: taxe,
    total: sousTotal - remise + taxe,
    notes: document.getElementById('inv-notes')?.value || '',
    conditions: document.getElementById('inv-conditions')?.value || '',
    items: items,
    created_offline: new Date().toISOString()
  };
  
  if (editingInvoiceIdx !== null) {
    invoice.offline_id = offlineInvoices[editingInvoiceIdx].offline_id;
    invoice.numero = offlineInvoices[editingInvoiceIdx].numero;
    offlineInvoices[editingInvoiceIdx] = invoice;
    editingInvoiceIdx = null;
  } else {
    offlineInvoices.push(invoice);
  }
  
  document.getElementById('invoice-form-container').innerHTML = '';
  renderInvoicesList();
  updateExportStats();
}

function renderInvoicesList() {
  const container = document.getElementById('invoices-list');
  if (offlineInvoices.length === 0) {
    container.innerHTML = '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucune facture créée. Cliquez sur "+ Nouvelle facture" pour commencer.</p>';
    return;
  }
  container.innerHTML = offlineInvoices.map((inv, i) => \`
    <div class="card" style="border-left:4px solid \${inv.type === 'proforma' ? '#f59e0b' : '#4f46e5'};margin-bottom:8px;padding:12px 16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <div>
          <strong>\${inv.numero}</strong>
          <span class="badge badge-amber" style="margin-left:8px;">Brouillon</span>
          \${inv.type === 'proforma' ? '<span class="badge" style="background:#fef3c7;color:#92400e;margin-left:4px;">Proforma</span>' : ''}
          <div style="font-size:0.8em;color:#6b7280;">
            \${inv.client_nom || 'Sans client'} • \${inv.date_facture}\${inv.date_echeance ? ' → Éch. ' + inv.date_echeance : ''} • \${inv.items.length} ligne(s)
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          <strong style="font-size:1.1em;">\${inv.total.toLocaleString('fr-FR')} ${devise}</strong>
          <button class="btn btn-secondary btn-sm" onclick="editInvoice(\${i})" title="Modifier"></button>
          <button class="btn btn-secondary btn-sm" onclick="previewInvoice(\${i})" title="Aperçu"></button>
          <button class="btn btn-danger btn-sm" onclick="deleteInvoice(\${i})" title="Supprimer"></button>
        </div>
      </div>
    </div>
  \`).join('');
}

function editInvoice(idx) {
  editingInvoiceIdx = idx;
  showInvoiceForm();
}

function deleteInvoice(idx) {
  if (!confirm('Supprimer cette facture brouillon ?')) return;
  offlineInvoices.splice(idx, 1);
  renderInvoicesList();
  updateExportStats();
}

function previewInvoice(idx) {
  const inv = offlineInvoices[idx];
  if (!inv) return;
  
  const invoiceTitle = inv.type === 'proforma' ? 'FACTURE PROFORMA' : 'FACTURE';
  
  const container = document.getElementById('invoice-preview-container');
  container.innerHTML = \`
    <div class="invoice-preview" style="margin-top:16px;" id="printable-invoice">
      <div class="invoice-header">
        <div class="invoice-company">
          \${LOGO_BASE64 ? '<img src="' + LOGO_BASE64 + '" alt="Logo">' : ''}
          <div>
            <h3 style="font-size:1.2em;font-weight:700;">\${esc(SETTINGS.nom_entreprise || 'Mon Entreprise')}</h3>
            \${SETTINGS.adresse ? '<div style="color:#6b7280;font-size:0.85em;">' + esc(SETTINGS.adresse) + '</div>' : ''}
            \${SETTINGS.telephone ? '<div style="color:#6b7280;font-size:0.85em;">' + esc(SETTINGS.telephone) + '</div>' : ''}
            \${SETTINGS.email ? '<div style="color:#6b7280;font-size:0.85em;">' + esc(SETTINGS.email) + '</div>' : ''}
            \${SETTINGS.numero_registre ? '<div style="color:#9ca3af;font-size:0.8em;">RCCM: ' + esc(SETTINGS.numero_registre) + '</div>' : ''}
            \${SETTINGS.numero_fiscal ? '<div style="color:#9ca3af;font-size:0.8em;">NIF: ' + esc(SETTINGS.numero_fiscal) + '</div>' : ''}
          </div>
        </div>
        <div class="invoice-title">
          <h2>\${invoiceTitle}</h2>
          <div style="color:#6b7280;">\${inv.numero}</div>
          <div style="font-size:0.85em;color:#6b7280;margin-top:4px;">Date: \${new Date(inv.date_facture).toLocaleDateString('fr-FR')}</div>
          \${inv.date_echeance ? '<div style="font-size:0.85em;color:#6b7280;">Échéance: ' + new Date(inv.date_echeance).toLocaleDateString('fr-FR') + '</div>' : ''}
        </div>
      </div>
      
      \${inv.client_nom ? \`
      <div style="background:#f9fafb;padding:16px;border-radius:8px;margin-bottom:16px;">
        <div style="font-size:0.75em;font-weight:600;color:#6b7280;text-transform:uppercase;margin-bottom:4px;">Facturé à</div>
        <div style="font-weight:600;">\${esc(inv.client_nom)}</div>
      </div>\` : ''}
      
      <table style="margin-bottom:16px;">
        <thead>
          <tr>
            <th>Description</th>
            <th style="text-align:center;width:60px;">Qté</th>
            <th style="text-align:right;width:120px;">Prix unit.</th>
            <th style="text-align:right;width:120px;">Total</th>
          </tr>
        </thead>
        <tbody>
          \${inv.items.map(item => \`
          <tr>
            <td>\${esc(item.description)}</td>
            <td style="text-align:center;">\${item.quantite}</td>
            <td style="text-align:right;">\${parseFloat(item.prix_unitaire).toLocaleString('fr-FR')} ${devise}</td>
            <td style="text-align:right;font-weight:600;">\${item.total.toLocaleString('fr-FR')} ${devise}</td>
          </tr>\`).join('')}
        </tbody>
      </table>
      
      <div class="totals">
        <div class="totals-box">
          <div class="totals-row"><span>Sous-total</span><span>\${inv.sous_total.toLocaleString('fr-FR')} ${devise}</span></div>
          \${inv.remise > 0 ? '<div class="totals-row" style="color:#dc2626;"><span>Remise</span><span>-' + inv.remise.toLocaleString('fr-FR') + ' ${devise}</span></div>' : ''}
          \${inv.taxe > 0 ? '<div class="totals-row"><span>Taxe</span><span>' + inv.taxe.toLocaleString('fr-FR') + ' ${devise}</span></div>' : ''}
          <div class="totals-row total"><span>TOTAL</span><span>\${inv.total.toLocaleString('fr-FR')} ${devise}</span></div>
        </div>
      </div>
      
      \${inv.notes ? '<div style="margin-top:16px;padding-top:16px;border-top:1px solid #e5e7eb;"><strong style="font-size:0.85em;color:#6b7280;">Notes:</strong><p style="font-size:0.85em;color:#6b7280;">' + esc(inv.notes) + '</p></div>' : ''}
      
      \${inv.conditions ? '<div style="margin-top:12px;"><strong style="font-size:0.8em;color:#6b7280;">Conditions de paiement:</strong><p style="font-size:0.8em;color:#9ca3af;">' + esc(inv.conditions) + '</p></div>' : (SETTINGS.conditions_paiement ? '<div style="margin-top:12px;"><strong style="font-size:0.8em;color:#6b7280;">Conditions de paiement:</strong><p style="font-size:0.8em;color:#9ca3af;">' + esc(SETTINGS.conditions_paiement) + '</p></div>' : '')}
      \${SETTINGS.mentions_legales ? '<div style="margin-top:8px;font-size:0.75em;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">' + esc(SETTINGS.mentions_legales) + '</div>' : ''}
    </div>
    <div class="no-print" style="margin-top:12px;display:flex;gap:8px;">
      <button class="btn btn-primary" onclick="printInvoice()">Imprimer</button>
      <button class="btn btn-secondary" onclick="document.getElementById('invoice-preview-container').innerHTML=''">Fermer</button>
    </div>
  \`;
}

function printInvoice() {
  const content = document.getElementById('printable-invoice');
  if (!content) return;
  const win = window.open('', '_blank');
  win.document.write('<html><head><title>Facture</title><style>');
  win.document.write(document.querySelector('style').textContent);
  win.document.write('</style></head><body style="background:white;padding:20px;">');
  win.document.write(content.outerHTML);
  win.document.write('</body></html>');
  win.document.close();
  win.print();
}

// ==========================================
// DEPENSES
// ==========================================
function showExpenseForm() {
  const container = document.getElementById('expense-form-container');
  container.innerHTML = \`
    <div style="border:2px solid #059669;border-radius:12px;padding:16px;margin-top:12px;">
      <h3>Nouvelle dépense</h3>
      <div class="grid-2">
        <div class="form-group">
          <label>Catégorie *</label>
          <select id="exp-categorie">
            <option value="Fournitures">Fournitures</option>
            <option value="Transport">Transport</option>
            <option value="Loyer">Loyer</option>
            <option value="Salaires">Salaires</option>
            <option value="Services">Services</option>
            <option value="Marketing">Marketing</option>
            <option value="Équipement">Équipement</option>
            <option value="Alimentation">Alimentation</option>
            <option value="Autre">Autre</option>
          </select>
        </div>
        <div class="form-group">
          <label>Montant (${devise}) *</label>
          <input type="number" id="exp-montant" min="0" placeholder="0">
        </div>
      </div>
      <div class="form-group">
        <label>Description *</label>
        <input type="text" id="exp-description" placeholder="Description de la dépense...">
      </div>
      <div class="grid-2">
        <div class="form-group">
          <label>Date</label>
          <input type="date" id="exp-date" value="\${new Date().toISOString().slice(0,10)}">
        </div>
        <div class="form-group">
          <label>Mode de paiement</label>
          <select id="exp-methode">
            <option value="especes">Espèces</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="virement">Virement</option>
            <option value="carte">Carte</option>
          </select>
        </div>
      </div>
      <div class="form-group">
        <label>Fournisseur</label>
        <input type="text" id="exp-fournisseur" placeholder="Nom du fournisseur (optionnel)">
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
        <button class="btn btn-secondary" onclick="document.getElementById('expense-form-container').innerHTML=''">Annuler</button>
        <button class="btn btn-success" onclick="saveExpense()">Enregistrer</button>
      </div>
    </div>
  \`;
}

function saveExpense() {
  const description = document.getElementById('exp-description')?.value;
  const montant = parseFloat(document.getElementById('exp-montant')?.value);
  if (!description || !montant) { toast.warning('Description et montant sont obligatoires'); return; }
  
  const categorie = document.getElementById('exp-categorie')?.value || 'Autre';
  const dateDepense = document.getElementById('exp-date')?.value || new Date().toISOString().slice(0, 10);
  const methode = document.getElementById('exp-methode')?.value || 'especes';
  const fournisseur = document.getElementById('exp-fournisseur')?.value || null;
  const expenseId = 'exp_' + Date.now();
  
  // 1. Créer la dépense
  offlineExpenses.push({
    offline_id: expenseId,
    categorie: categorie,
    description: description,
    montant: montant,
    date_depense: dateDepense,
    methode_paiement: methode,
    fournisseur: fournisseur,
    created_offline: new Date().toISOString()
  });
  
  // 2. Créer automatiquement le décaissement associé
  offlinePayments.push({
    offline_id: 'pay_' + Date.now(),
    linked_expense_id: expenseId,
    type: 'decaissement',
    categorie: categorie,
    montant: montant,
    methode: methode,
    mode_paiement: methode,
    date_paiement: dateDepense,
    description: 'Dépense: ' + description,
    notes: 'Décaissement auto - ' + (fournisseur ? fournisseur + ' - ' : '') + description,
    fournisseur: fournisseur,
    source: 'expense',
    source_id: expenseId,
    created_offline: new Date().toISOString()
  });
  
  document.getElementById('expense-form-container').innerHTML = '';
  renderExpensesList();
  renderPaymentsList();
  updateExportStats();
}

function renderExpensesList() {
  const container = document.getElementById('expenses-list');
  if (offlineExpenses.length === 0) {
    container.innerHTML = '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucune dépense enregistrée. Cliquez sur "+ Nouvelle dépense".</p>';
    return;
  }
  
  const total = offlineExpenses.reduce((s, e) => s + e.montant, 0);
  container.innerHTML = \`
    <div class="alert alert-info" style="margin-bottom:12px;">Total dépenses hors ligne: <strong>\${total.toLocaleString('fr-FR')} ${devise}</strong></div>
    <table>
      <thead><tr><th>Date</th><th>Catégorie</th><th>Description</th><th>Fournisseur</th><th>Montant</th><th></th></tr></thead>
      <tbody>
        \${offlineExpenses.map((e, i) => \`
        <tr>
          <td>\${e.date_depense}</td>
          <td><span class="badge badge-gray">\${esc(e.categorie)}</span></td>
          <td>\${esc(e.description)}</td>
          <td>\${e.fournisseur || '-'}</td>
          <td style="font-weight:600;color:#dc2626;">\${e.montant.toLocaleString('fr-FR')} ${devise}</td>
          <td><button class="btn btn-danger btn-sm" onclick="deleteExpense(\${i})"></button></td>
        </tr>\`).join('')}
      </tbody>
    </table>
  \`;
}

function deleteExpense(idx) {
  if (!confirm('Supprimer cette dépense ?')) return;
  const expense = offlineExpenses[idx];
  // Supprimer aussi le décaissement lié
  if (expense && expense.offline_id) {
    const payIdx = offlinePayments.findIndex(p => p.linked_expense_id === expense.offline_id);
    if (payIdx !== -1) offlinePayments.splice(payIdx, 1);
  }
  offlineExpenses.splice(idx, 1);
  renderExpensesList();
  renderPaymentsList();
  updateExportStats();
}

// ==========================================
// PAIEMENTS (auto-générés)
// ==========================================
function renderPaymentsList() {
  const container = document.getElementById('payments-list');
  if (!container) return;
  if (offlinePayments.length === 0) {
    container.innerHTML = '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucun paiement. Les décaissements seront créés automatiquement avec les dépenses.</p>';
    return;
  }
  
  const totalDec = offlinePayments.filter(p => p.type === 'decaissement').reduce((s, p) => s + p.montant, 0);
  container.innerHTML = \`
    <div class="alert" style="background:#fef2f2;border-left:4px solid #dc2626;padding:12px 16px;margin-bottom:12px;">
      Total décaissements: <strong style="color:#dc2626;">\${totalDec.toLocaleString('fr-FR')} \${DEVISE}</strong> (\${offlinePayments.length} paiement(s))
    </div>
    <table>
      <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Mode</th><th>Montant</th></tr></thead>
      <tbody>
        \${offlinePayments.map((p, i) => \`
        <tr>
          <td>\${p.date_paiement}</td>
          <td><span class="badge" style="background:#fef2f2;color:#dc2626;">Décaissement</span></td>
          <td>\${esc(p.description || p.notes || '-')}</td>
          <td>\${p.mode_paiement === 'especes' ? 'Espèces' : p.mode_paiement === 'mobile_money' ? 'Mobile Money' : p.mode_paiement === 'virement' ? 'Virement' : p.mode_paiement === 'carte' ? 'Carte' : p.mode_paiement}</td>
          <td style="font-weight:600;color:#dc2626;">-\${p.montant.toLocaleString('fr-FR')} \${DEVISE}</td>
        </tr>\`).join('')}
      </tbody>
    </table>
  \`;
}

// ==========================================
// CLIENTS
// ==========================================
function showClientForm() {
  const container = document.getElementById('client-form-container');
  container.innerHTML = \`
    <div style="border:2px solid #4f46e5;border-radius:12px;padding:16px;margin-top:12px;">
      <h3>Nouveau client</h3>
      <div class="grid-2">
        <div class="form-group"><label>Nom *</label><input type="text" id="cl-nom" placeholder="Nom du client"></div>
        <div class="form-group"><label>Téléphone</label><input type="text" id="cl-telephone" placeholder="Numéro de téléphone"></div>
      </div>
      <div class="grid-2">
        <div class="form-group"><label>Email</label><input type="email" id="cl-email" placeholder="Email"></div>
        <div class="form-group"><label>Adresse</label><input type="text" id="cl-adresse" placeholder="Adresse"></div>
      </div>
      <div class="form-group"><label>Notes</label><textarea id="cl-notes" placeholder="Notes..."></textarea></div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
        <button class="btn btn-secondary" onclick="document.getElementById('client-form-container').innerHTML=''">Annuler</button>
        <button class="btn btn-primary" onclick="saveClient()">Enregistrer</button>
      </div>
    </div>
  \`;
}

function saveClient() {
  const nom = document.getElementById('cl-nom')?.value;
  if (!nom) { toast.warning('Le nom est obligatoire'); return; }
  
  const client = {
    offline_id: 'offline_cl_' + Date.now(),
    nom: nom,
    telephone: document.getElementById('cl-telephone')?.value || null,
    email: document.getElementById('cl-email')?.value || null,
    adresse: document.getElementById('cl-adresse')?.value || null,
    notes: document.getElementById('cl-notes')?.value || null,
    created_offline: new Date().toISOString()
  };
  
  offlineClients.push(client);
  document.getElementById('client-form-container').innerHTML = '';
  
  // Ajouter au tableau des clients
  const tbody = document.getElementById('clients-table');
  const tr = document.createElement('tr');
  tr.className = 'client-row';
  tr.dataset.search = (client.nom + ' ' + (client.telephone || '') + ' ' + (client.email || '')).toLowerCase();
  tr.innerHTML = \`
    <td style="font-weight:600;">\${esc(client.nom)} <span class="badge badge-amber">Nouveau</span></td>
    <td>\${client.telephone || '-'}</td>
    <td>\${client.email || '-'}</td>
    <td>\${client.adresse || '-'}</td>
  \`;
  tbody.appendChild(tr);
  
  updateExportStats();
  toast.success('Client "' + nom + '" ajouté ! Il sera disponible dans les formulaires de facture.');
}

// ==========================================
// PRESTATIONS
// ==========================================
function showPrestationForm() {
  const allClients = getAllClients();
  const container = document.getElementById('prestation-form-container');
  container.innerHTML = \`
    <div style="border:2px solid #7c3aed;border-radius:12px;padding:16px;margin-top:12px;">
      <h3>Nouvelle prestation</h3>
      <div class="form-group"><label>Titre *</label><input type="text" id="prest-titre" placeholder="Titre de la prestation"></div>
      <div class="grid-2">
        <div class="form-group">
          <label>Client</label>
          <select id="prest-client">
            <option value="">-- Sans client --</option>
            \${allClients.map(c => \`<option value="\${c.id || c.offline_id}" data-is-offline="\${c.offline_id ? '1' : '0'}">\${esc(c.nom)}</option>\`).join('')}
          </select>
        </div>
        <div class="form-group"><label>Montant (${devise})</label><input type="number" id="prest-montant" min="0" value="0"></div>
      </div>
      <div class="grid-2">
        <div class="form-group"><label>Date début</label><input type="date" id="prest-debut" value="\${new Date().toISOString().slice(0,10)}"></div>
        <div class="form-group"><label>Date fin prévue</label><input type="date" id="prest-fin"></div>
      </div>
      <div class="form-group"><label>Description</label><textarea id="prest-desc" placeholder="Description de la prestation..."></textarea></div>
      <div class="form-group"><label>Notes</label><textarea id="prest-notes" placeholder="Notes..."></textarea></div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
        <button class="btn btn-secondary" onclick="document.getElementById('prestation-form-container').innerHTML=''">Annuler</button>
        <button class="btn btn-primary" onclick="savePrestation()">Enregistrer</button>
      </div>
    </div>
  \`;
}

function savePrestation() {
  const titre = document.getElementById('prest-titre')?.value;
  if (!titre) { toast.warning('Le titre est obligatoire'); return; }
  
  const clientSelect = document.getElementById('prest-client');
  const clientId = clientSelect.value || null;
  const clientOption = clientSelect.selectedOptions[0];
  const isOfflineClient = clientOption?.dataset?.isOffline === '1';
  
  offlinePrestations.push({
    titre: titre,
    client_id: isOfflineClient ? 'offline_' + clientId : clientId,
    client_nom: clientId ? clientOption.textContent : null,
    montant: parseFloat(document.getElementById('prest-montant')?.value) || 0,
    description: document.getElementById('prest-desc')?.value || null,
    date_debut: document.getElementById('prest-debut')?.value || null,
    date_fin_prevue: document.getElementById('prest-fin')?.value || null,
    notes: document.getElementById('prest-notes')?.value || null,
    created_offline: new Date().toISOString()
  });
  
  document.getElementById('prestation-form-container').innerHTML = '';
  renderPrestationsList();
  updateExportStats();
}

function renderPrestationsList() {
  const container = document.getElementById('new-prestations-list');
  if (offlinePrestations.length === 0) { container.innerHTML = ''; return; }
  
  container.innerHTML = \`
    <h3 style="margin-bottom:8px;">Nouvelles prestations (hors ligne)</h3>
    <table>
      <thead><tr><th>Titre</th><th>Client</th><th>Montant</th><th>Début</th><th></th></tr></thead>
      <tbody>
        \${offlinePrestations.map((p, i) => \`
        <tr>
          <td style="font-weight:600;">\${esc(p.titre)}</td>
          <td>\${p.client_nom || '-'}</td>
          <td>\${p.montant.toLocaleString('fr-FR')} ${devise}</td>
          <td>\${p.date_debut || '-'}</td>
          <td><button class="btn btn-danger btn-sm" onclick="deletePrestation(\${i})"></button></td>
        </tr>\`).join('')}
      </tbody>
    </table>
  \`;
}

function deletePrestation(idx) {
  if (!confirm('Supprimer cette prestation ?')) return;
  offlinePrestations.splice(idx, 1);
  renderPrestationsList();
  updateExportStats();
}

// ==========================================
// INVENTAIRE
// ==========================================
let editingInventoryId = null;

function showInventoryForm(inventoryId) {
  const inv = EXISTING_INVENTORIES.find(i => String(i.id) === String(inventoryId));
  if (!inv) return;
  editingInventoryId = inventoryId;

  const container = document.getElementById('inventory-form-container');
  const savedItems = updatedInventories[inventoryId]?.items || null;

  // Construire les items: saved > existants de l'inventaire > produits
  const existingItemsMap = {};
  (inv.items || []).forEach(item => { existingItemsMap[item.product_id] = item; });
  if (savedItems) {
    savedItems.forEach(item => { existingItemsMap[item.product_id] = item; });
  }

  const items = PRODUCTS.map(p => {
    const existing = existingItemsMap[p.id];
    return {
      product_id: p.id,
      product_nom: p.nom,
      product_code: p.code_produit || '',
      stock_theorique: existing ? parseInt(existing.stock_theorique) : (parseInt(p.stock_actuel) || 0),
      stock_compte: existing && existing.stock_compte !== null && existing.stock_compte !== '' && String(existing.stock_compte) !== '0' ? existing.stock_compte : (existing && String(existing.stock_compte) === '0' ? 0 : ''),
      ecart: existing ? parseInt(existing.ecart || 0) : 0,
      prix_unitaire: existing ? parseFloat(existing.prix_unitaire || 0) : parseFloat(p.prix_achat || p.prix_vente || 0),
      valeur_ecart: existing ? parseFloat(existing.valeur_ecart || 0) : 0,
      notes: existing ? (existing.notes || '') : ''
    };
  });

  if (!items || items.length === 0) {
    container.innerHTML = '<div class="alert alert-warning">Aucun produit disponible.</div>';
    return;
  }

  container.innerHTML = \`
    <div style="margin-top:16px;">
      <div class="inv-form-header" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
        <h3 style="font-size:1.2em;font-weight:700;">Comptage - \${esc(inv.reference || 'Inventaire')}</h3>
        <div class="inv-form-actions" style="display:flex;gap:8px;">
          <button class="btn btn-secondary" onclick="cancelInventoryForm()">Annuler</button>
          <button class="btn btn-success" onclick="saveInventory()">Enregistrer</button>
        </div>
      </div>

      <div class="form-group" style="margin-bottom:16px;">
          <label>Notes</label>
          <input type="text" id="inv-notes-field" value="\${esc(inv.notes || '')}" placeholder="Ex: Inventaire mensuel entrepôt...">
        </div>

      <div style="display:flex;gap:12px;margin-bottom:12px;flex-wrap:wrap;align-items:center;">
        <input type="text" class="search-box" placeholder="Rechercher un produit..." oninput="filterInventoryItems(this.value)" style="flex:1;min-width:200px;margin-bottom:0;">
        <div style="display:flex;gap:8px;">
          <button class="btn btn-secondary btn-sm" onclick="fillAllZero()">Tout à 0</button>
          <button class="btn btn-secondary btn-sm" onclick="fillAllTheorique()">= Théorique</button>
        </div>
      </div>

      <div class="grid-4" style="margin-bottom:12px;">
        <div class="stat-card"><div class="number" id="inv-stat-total">\${items.length}</div><div class="label">Produits</div></div>
        <div class="stat-card"><div class="number" id="inv-stat-counted">0</div><div class="label">Comptés</div></div>
        <div class="stat-card"><div class="number" id="inv-stat-ecarts">0</div><div class="label">Écarts</div></div>
        <div class="stat-card"><div class="number" id="inv-stat-valeur">0</div><div class="label">Valeur écart</div></div>
      </div>

      <!-- Desktop: table view -->
      <div style="overflow-x:auto;">
        <table id="inventory-items-table">
          <thead>
            <tr>
              <th style="width:30%;">Produit</th>
              <th style="text-align:center;">Stock théorique</th>
              <th style="text-align:center;width:120px;">Stock compté</th>
              <th style="text-align:center;">Écart</th>
              <th style="text-align:right;">Valeur écart</th>
              <th style="width:15%;">Notes</th>
            </tr>
          </thead>
          <tbody>
            \${items.map((item, i) => \`
            <tr class="inv-item-row" data-search="\${(item.product_nom + ' ' + (item.product_code || '')).toLowerCase()}" data-idx="\${i}">
              <td>
                <div style="font-weight:600;">\${esc(item.product_nom)}</div>
                \${item.product_code ? '<div style="font-size:0.75em;color:#6b7280;">#' + esc(item.product_code) + '</div>' : ''}
              </td>
              <td style="text-align:center;font-weight:600;">\${item.stock_theorique}</td>
              <td style="text-align:center;">
                <input type="number" min="0" value="\${item.stock_compte !== '' ? item.stock_compte : ''}" placeholder="-"
                  class="inv-item-count" data-idx="\${i}"
                  oninput="updateInventoryItemEcart(\${i}, this)"
                  style="width:80px;padding:6px 8px;border:1px solid #d1d5db;border-radius:8px;text-align:center;font-size:0.9em;">
              </td>
              <td style="text-align:center;" class="inv-item-ecart" id="inv-ecart-\${i}">-</td>
              <td style="text-align:right;" class="inv-item-valeur" id="inv-valeur-\${i}">-</td>
              <td>
                <input type="text" value="\${esc(item.notes || '')}" placeholder="..." class="inv-item-notes" data-idx="\${i}"
                  style="width:100%;padding:4px 8px;border:1px solid #d1d5db;border-radius:6px;font-size:0.8em;">
              </td>
            </tr>\`).join('')}
          </tbody>
        </table>
      </div>

      <!-- Mobile: card view -->
      <div class="inv-mobile-cards" id="inv-mobile-cards" style="display:none;">
        \${items.map((item, i) => \`
        <div class="inv-mobile-card inv-item-row-m" data-search="\${(item.product_nom + ' ' + (item.product_code || '')).toLowerCase()}" data-idx="\${i}">
          <div class="imc-header">
            <div>
              <div class="imc-name">\${esc(item.product_nom)}</div>
              \${item.product_code ? '<div class="imc-code">#' + esc(item.product_code) + '</div>' : ''}
            </div>
            <span id="inv-ecart-m-\${i}" style="font-weight:700;font-size:0.85em;color:#6b7280;">-</span>
          </div>
          <div class="imc-grid">
            <div>
              <div class="imc-label">Théorique</div>
              <div class="imc-val">\${item.stock_theorique}</div>
            </div>
            <div>
              <div class="imc-label">Compté</div>
              <input type="number" min="0" inputmode="numeric" value="\${item.stock_compte !== '' ? item.stock_compte : ''}" placeholder="-"
                class="imc-input inv-item-count-m" data-idx="\${i}"
                oninput="updateInventoryItemEcart(\${i}, this)">
            </div>
            <div>
              <div class="imc-label">Valeur écart</div>
              <div class="imc-val" id="inv-valeur-m-\${i}" style="font-size:0.8em;">-</div>
            </div>
          </div>
          <input type="text" value="\${esc(item.notes || '')}" placeholder="Notes..." class="imc-notes inv-item-notes-m" data-idx="\${i}">
        </div>\`).join('')}
      </div>
    </div>
  \`;

  // Store items data for JS access
  window._inventoryItems = items;
  updateInventoryStats();
}

function cancelInventoryForm() {
  editingInventoryId = null;
  document.getElementById('inventory-form-container').innerHTML = '';
}

function filterInventoryItems(query) {
  const q = query.toLowerCase();
  document.querySelectorAll('.inv-item-row').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
  document.querySelectorAll('.inv-item-row-m').forEach(row => {
    row.style.display = row.dataset.search.includes(q) ? '' : 'none';
  });
}

function fillAllZero() {
  document.querySelectorAll('.inv-item-count').forEach(input => {
    input.value = 0;
    updateInventoryItemEcart(parseInt(input.dataset.idx), input);
  });
  document.querySelectorAll('.inv-item-count-m').forEach(input => {
    input.value = 0;
  });
}

function fillAllTheorique() {
  if (!window._inventoryItems) return;
  document.querySelectorAll('.inv-item-count').forEach(input => {
    const idx = parseInt(input.dataset.idx);
    input.value = window._inventoryItems[idx]?.stock_theorique || 0;
    updateInventoryItemEcart(idx, input);
  });
  document.querySelectorAll('.inv-item-count-m').forEach(input => {
    const idx = parseInt(input.dataset.idx);
    input.value = window._inventoryItems[idx]?.stock_theorique || 0;
  });
}

function updateInventoryItemEcart(idx, sourceInput) {
  if (!window._inventoryItems || !window._inventoryItems[idx]) return;
  const item = window._inventoryItems[idx];
  
  // Use the source input (the one user typed in), fallback to whichever is visible
  const desktopInput = document.querySelectorAll('.inv-item-count')[idx];
  const mobileInput = document.querySelectorAll('.inv-item-count-m')[idx];
  const input = sourceInput || mobileInput || desktopInput;
  if (!input) return;

  // Sync the other input
  const val = input.value;
  if (desktopInput && desktopInput !== input) desktopInput.value = val;
  if (mobileInput && mobileInput !== input) mobileInput.value = val;

  const counted = val !== '' ? parseInt(val) : null;
  const ecartEl = document.getElementById('inv-ecart-' + idx);
  const valeurEl = document.getElementById('inv-valeur-' + idx);
  const ecartElM = document.getElementById('inv-ecart-m-' + idx);
  const valeurElM = document.getElementById('inv-valeur-m-' + idx);

  if (counted === null || isNaN(counted)) {
    if (ecartEl) ecartEl.innerHTML = '-';
    if (valeurEl) valeurEl.innerHTML = '-';
    if (ecartElM) ecartElM.innerHTML = '-';
    if (valeurElM) valeurElM.innerHTML = '-';
    item.stock_compte = '';
    item.ecart = 0;
    item.valeur_ecart = 0;
  } else {
    const ecart = counted - item.stock_theorique;
    const valeurEcart = ecart * item.prix_unitaire;
    item.stock_compte = counted;
    item.ecart = ecart;
    item.valeur_ecart = valeurEcart;
    const color = ecart > 0 ? '#059669' : ecart < 0 ? '#dc2626' : '#6b7280';
    const vColor = valeurEcart > 0 ? '#059669' : valeurEcart < 0 ? '#dc2626' : '#6b7280';
    const ecartText = (ecart > 0 ? '+' : '') + ecart;
    const valText = (valeurEcart > 0 ? '+' : '') + valeurEcart.toLocaleString('fr-FR') + ' ' + DEVISE;

    if (ecartEl) ecartEl.innerHTML = '<span style="font-weight:600;color:' + color + ';">' + ecartText + '</span>';
    if (valeurEl) valeurEl.innerHTML = '<span style="font-weight:600;color:' + vColor + ';">' + valText + '</span>';
    if (ecartElM) { ecartElM.innerHTML = ecartText; ecartElM.style.color = color; }
    if (valeurElM) { valeurElM.innerHTML = valText; valeurElM.style.color = vColor; }
  }
  updateInventoryStats();
}

function updateInventoryStats() {
  if (!window._inventoryItems) return;
  const items = window._inventoryItems;
  const counted = items.filter(i => i.stock_compte !== '' && i.stock_compte !== null).length;
  const ecarts = items.filter(i => i.ecart !== 0 && i.stock_compte !== '' && i.stock_compte !== null).length;
  const valeur = items.reduce((s, i) => s + (i.valeur_ecart || 0), 0);

  const el = (id) => document.getElementById(id);
  if (el('inv-stat-counted')) el('inv-stat-counted').textContent = counted;
  if (el('inv-stat-ecarts')) el('inv-stat-ecarts').textContent = ecarts;
  if (el('inv-stat-valeur')) {
    const color = valeur > 0 ? '#059669' : valeur < 0 ? '#dc2626' : '#4f46e5';
    el('inv-stat-valeur').textContent = (valeur > 0 ? '+' : '') + valeur.toLocaleString('fr-FR');
    el('inv-stat-valeur').style.color = color;
  }
}

function saveInventory() {
  if (!window._inventoryItems) return;

  // Collecter les notes depuis les inputs (desktop + mobile)
  document.querySelectorAll('.inv-item-notes').forEach(input => {
    const idx = parseInt(input.dataset.idx);
    if (window._inventoryItems[idx]) {
      window._inventoryItems[idx].notes = input.value || '';
    }
  });
  document.querySelectorAll('.inv-item-notes-m').forEach(input => {
    const idx = parseInt(input.dataset.idx);
    if (window._inventoryItems[idx]) {
      window._inventoryItems[idx].notes = input.value || '';
    }
  });

  const countedItems = window._inventoryItems.filter(i => i.stock_compte !== '' && i.stock_compte !== null);
  if (countedItems.length === 0) {
    toast.warning('Veuillez compter au moins un produit avant d\\'enregistrer.');
    return;
  }

  const notes = document.getElementById('inv-notes-field')?.value || '';

  if (!editingInventoryId) return;

  updatedInventories[editingInventoryId] = {
    id: editingInventoryId,
    notes: notes,
    total_produits: countedItems.length,
    total_ecarts: countedItems.filter(i => i.ecart !== 0).length,
    valeur_ecart: countedItems.reduce((s, i) => s + (i.valeur_ecart || 0), 0),
    items: countedItems.map(i => ({
      product_id: i.product_id,
      product_nom: i.product_nom,
      product_code: i.product_code,
      stock_theorique: i.stock_theorique,
      stock_compte: i.stock_compte,
      ecart: i.ecart,
      prix_unitaire: i.prix_unitaire,
      valeur_ecart: i.valeur_ecart,
      notes: i.notes || null
    })),
    completed_offline: new Date().toISOString()
  };

  editingInventoryId = null;
  document.getElementById('inventory-form-container').innerHTML = '';
  window._inventoryItems = null;
  renderInventoryList();
  updateExportStats();
}

function renderInventoryList() {
  const container = document.getElementById('inventory-list');
  if (!container) return;

  if (EXISTING_INVENTORIES.length === 0) {
    container.innerHTML = '<p style="text-align:center;color:#9ca3af;padding:24px;">Aucun inventaire en cours. Créez un inventaire depuis l\\'application en ligne, puis revenez ici pour le compléter hors réseau.</p>';
    return;
  }

  container.innerHTML = EXISTING_INVENTORIES.map((inv) => {
    const updated = updatedInventories[inv.id];
    const totalProduits = updated ? updated.total_produits : parseInt(inv.total_produits || 0);
    const totalEcarts = updated ? updated.total_ecarts : parseInt(inv.total_ecarts || 0);
    const valeurEcart = updated ? updated.valeur_ecart : parseFloat(inv.valeur_ecart || 0);
    const valColor = valeurEcart > 0 ? '#059669' : valeurEcart < 0 ? '#dc2626' : '#6b7280';
    const isSaved = !!updated;

    return \`
    <div class="card" style="border-left:4px solid \${isSaved ? '#059669' : '#4f46e5'};margin-bottom:8px;padding:12px 16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
        <div>
          <strong>\${esc(inv.reference || 'Inventaire')} - \${inv.date_inventaire}</strong>
          \${isSaved ? '<span class="badge badge-green" style="margin-left:8px;">Compté</span>' : '<span class="badge badge-blue" style="margin-left:8px;">En cours</span>'}
          <div style="font-size:0.8em;color:#6b7280;">
            \${totalProduits} produit(s) • \${totalEcarts} écart(s)
            \${inv.notes ? ' • ' + esc(inv.notes) : ''}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          \${isSaved ? '<span style="font-weight:600;color:' + valColor + ';">' + (valeurEcart > 0 ? '+' : '') + valeurEcart.toLocaleString('fr-FR') + ' ' + DEVISE + '</span>' : ''}
          \${isSaved ? \`<button class="btn btn-primary" onclick="showPrintInventoryModal('\${inv.id}')" style="padding:8px 12px;">Imprimer</button>\` : ''}
          <button class="btn \${isSaved ? 'btn-secondary' : 'btn-primary'}" onclick="showInventoryForm('\${inv.id}')">
            \${isSaved ? 'Modifier' : 'Continuer le comptage'}
          </button>
        </div>
      </div>
    </div>\`;
  }).join('');
}

// ==========================================
// IMPRESSION INVENTAIRE
// ==========================================
function showPrintInventoryModal(inventoryId) {
  const inv = EXISTING_INVENTORIES.find(i => String(i.id) === String(inventoryId));
  if (!inv) return;
  const updated = updatedInventories[inventoryId];
  if (!updated) { toast.warning('Veuillez d\\'abord compter et enregistrer cet inventaire.'); return; }

  const overlay = document.createElement('div');
  overlay.className = 'print-modal-overlay';
  overlay.id = 'print-inventory-modal';
  overlay.innerHTML = \`
    <div class="print-modal">
      <h3 style="margin-bottom:16px;font-size:1.1em;font-weight:700;">Imprimer l'inventaire</h3>
      <p style="font-size:0.85em;color:#6b7280;margin-bottom:16px;">Saisissez les informations de la personne ayant effectué le comptage.</p>
      <div class="form-group" style="margin-bottom:12px;">
        <label>Nom complet *</label>
        <input type="text" id="print-counter-name" placeholder="Ex: Jean Dupont" style="width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:8px;">
      </div>
      <div class="form-group" style="margin-bottom:12px;">
        <label>Fonction / Poste</label>
        <input type="text" id="print-counter-role" placeholder="Ex: Magasinier" style="width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:8px;">
      </div>
      <div class="form-group" style="margin-bottom:16px;">
        <label>Observations</label>
        <input type="text" id="print-counter-observations" placeholder="Ex: Comptage effectué en présence du responsable" style="width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:8px;">
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="closePrintInventoryModal()">Annuler</button>
        <button class="btn btn-primary" onclick="printInventory('\${inventoryId}')">Imprimer</button>
      </div>
    </div>
  \`;
  document.body.appendChild(overlay);
  document.getElementById('print-counter-name').focus();
}

function closePrintInventoryModal() {
  const m = document.getElementById('print-inventory-modal');
  if (m) m.remove();
}

function printInventory(inventoryId) {
  const inv = EXISTING_INVENTORIES.find(i => String(i.id) === String(inventoryId));
  const updated = updatedInventories[inventoryId];
  if (!inv || !updated) return;

  const counterName = (document.getElementById('print-counter-name')?.value || '').trim();
  const counterRole = (document.getElementById('print-counter-role')?.value || '').trim();
  const counterObs = (document.getElementById('print-counter-observations')?.value || '').trim();

  if (!counterName) { toast.warning('Veuillez saisir le nom de la personne ayant compté.'); return; }

  closePrintInventoryModal();

  const items = updated.items || [];
  const totalEcarts = items.filter(i => i.ecart !== 0).length;
  const valeurEcart = items.reduce((s, i) => s + (i.valeur_ecart || 0), 0);
  const now = new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' });

  const html = \`
    <html>
    <head><title>Inventaire - \${esc(inv.reference || 'Inventaire')}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #1f2937; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; }
      th { background: #f3f4f6; font-weight: 600; font-size: 0.85em; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #4f46e5; }
      .company-info h2 { font-size: 1.3em; color: #1f2937; }
      .company-info div { color: #6b7280; font-size: 0.9em; margin-top: 2px; }
      .inv-title { text-align: right; }
      .inv-title h1 { font-size: 1.4em; color: #4f46e5; }
      .inv-title div { font-size: 0.9em; color: #6b7280; margin-top: 2px; }
      .summary { display: flex; gap: 16px; margin: 16px 0; }
      .summary-box { flex: 1; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center; }
      .summary-box .val { font-size: 1.3em; font-weight: 700; color: #4f46e5; }
      .summary-box .lbl { font-size: 0.8em; color: #6b7280; margin-top: 2px; }
      .ecart-pos { color: #059669; font-weight: 600; }
      .ecart-neg { color: #dc2626; font-weight: 600; }
      .ecart-zero { color: #6b7280; }
      .counter-section { margin-top: 24px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; }
      .counter-section h3 { font-size: 1em; margin-bottom: 12px; color: #374151; }
      .counter-grid { display: flex; gap: 24px; flex-wrap: wrap; }
      .counter-field { flex: 1; min-width: 200px; }
      .counter-field .label { font-size: 0.8em; color: #6b7280; margin-bottom: 2px; }
      .counter-field .value { font-weight: 600; font-size: 0.95em; padding: 6px 0; border-bottom: 1px solid #d1d5db; }
      .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
      .sig-block { text-align: center; width: 40%; }
      .sig-line { border-top: 1px solid #9ca3af; margin-top: 50px; padding-top: 4px; font-size: 0.85em; color: #6b7280; }
      .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 0.8em; color: #9ca3af; }
      .logo-img { max-height: 50px; max-width: 120px; margin-bottom: 8px; }
      @media print { @page { size: A4; margin: 10mm; } }
    </style>
    </head>
    <body>
      <div class="header">
        <div class="company-info">
          \${LOGO_BASE64 ? '<img src="' + LOGO_BASE64 + '" class="logo-img" alt="Logo">' : ''}
          <h2>\${esc(SETTINGS.nom_entreprise || 'Mon Entreprise')}</h2>
          \${SETTINGS.adresse ? '<div>' + esc(SETTINGS.adresse) + '</div>' : ''}
          \${SETTINGS.telephone ? '<div>' + esc(SETTINGS.telephone) + '</div>' : ''}
          \${SETTINGS.email ? '<div>' + esc(SETTINGS.email) + '</div>' : ''}
          \${SETTINGS.numero_registre ? '<div style="font-size:0.85em;color:#9ca3af;">RCCM: ' + esc(SETTINGS.numero_registre) + '</div>' : ''}
          \${SETTINGS.numero_fiscal ? '<div style="font-size:0.85em;color:#9ca3af;">NIF: ' + esc(SETTINGS.numero_fiscal) + '</div>' : ''}
        </div>
        <div class="inv-title">
          <h1>FICHE D'INVENTAIRE</h1>
          <div><strong>Réf:</strong> \${esc(inv.reference || '-')}</div>
          <div><strong>Date inventaire:</strong> \${inv.date_inventaire || '-'}</div>
          <div><strong>Statut:</strong> \${inv.statut || 'en_cours'}</div>
          \${updated.notes ? '<div><strong>Notes:</strong> ' + esc(updated.notes) + '</div>' : ''}
        </div>
      </div>

      <div class="summary">
        <div class="summary-box"><div class="val">\${items.length}</div><div class="lbl">Produits comptés</div></div>
        <div class="summary-box"><div class="val">\${totalEcarts}</div><div class="lbl">Écarts détectés</div></div>
        <div class="summary-box"><div class="val" style="color:\${valeurEcart > 0 ? '#059669' : valeurEcart < 0 ? '#dc2626' : '#4f46e5'}">\${(valeurEcart > 0 ? '+' : '') + valeurEcart.toLocaleString('fr-FR')} \${DEVISE}</div><div class="lbl">Valeur des écarts</div></div>
      </div>

      <table>
        <thead>
          <tr>
            <th style="width:5%;">#</th>
            <th style="width:25%;">Produit</th>
            <th style="width:10%;">Code</th>
            <th style="text-align:center;width:12%;">Stock théorique</th>
            <th style="text-align:center;width:12%;">Stock compté</th>
            <th style="text-align:center;width:10%;">Écart</th>
            <th style="text-align:right;width:13%;">Valeur écart</th>
            <th style="width:13%;">Notes</th>
          </tr>
        </thead>
        <tbody>
          \${items.map((item, i) => \`
          <tr>
            <td>\${i + 1}</td>
            <td style="font-weight:600;">\${esc(item.product_nom)}</td>
            <td>\${esc(item.product_code || '-')}</td>
            <td style="text-align:center;">\${item.stock_theorique}</td>
            <td style="text-align:center;font-weight:600;">\${item.stock_compte}</td>
            <td style="text-align:center;" class="\${item.ecart > 0 ? 'ecart-pos' : item.ecart < 0 ? 'ecart-neg' : 'ecart-zero'}">\${(item.ecart > 0 ? '+' : '') + item.ecart}</td>
            <td style="text-align:right;" class="\${item.valeur_ecart > 0 ? 'ecart-pos' : item.valeur_ecart < 0 ? 'ecart-neg' : 'ecart-zero'}">\${(item.valeur_ecart > 0 ? '+' : '') + parseFloat(item.valeur_ecart || 0).toLocaleString('fr-FR')} \${DEVISE}</td>
            <td style="font-size:0.85em;color:#6b7280;">\${esc(item.notes || '')}</td>
          </tr>\`).join('')}
        </tbody>
      </table>

      <div class="counter-section">
        <h3>Informations du compteur</h3>
        <div class="counter-grid">
          <div class="counter-field">
            <div class="label">Nom complet</div>
            <div class="value">\${esc(counterName)}</div>
          </div>
          <div class="counter-field">
            <div class="label">Fonction / Poste</div>
            <div class="value">\${esc(counterRole || '-')}</div>
          </div>
          <div class="counter-field">
            <div class="label">Date du comptage</div>
            <div class="value">\${now}</div>
          </div>
        </div>
        \${counterObs ? '<div style="margin-top:12px;"><div class="label">Observations</div><div class="value">' + esc(counterObs) + '</div></div>' : ''}
      </div>

      <div class="signatures">
        <div class="sig-block">
          <div class="sig-line">Signature du compteur</div>
          <div style="font-size:0.85em;margin-top:4px;">\${esc(counterName)}</div>
        </div>
        <div class="sig-block">
          <div class="sig-line">Signature du responsable</div>
        </div>
      </div>

      <div class="footer">
        \${esc(SETTINGS.nom_entreprise || '')} — Fiche d'inventaire imprimée le \${now}
        \${SETTINGS.mentions_legales ? '<br>' + esc(SETTINGS.mentions_legales) : ''}
      </div>
    </body>
    </html>
  \`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.print();
}

// ==========================================
// EXPORT
// ==========================================
function updateExportStats() {
  const el = (id) => document.getElementById(id);
  if (el('export-invoices-count')) el('export-invoices-count').textContent = offlineInvoices.length;
  if (el('export-expenses-count')) el('export-expenses-count').textContent = offlineExpenses.length;
  if (el('export-payments-count')) el('export-payments-count').textContent = offlinePayments.length;
  if (el('export-clients-count')) el('export-clients-count').textContent = offlineClients.length;
  if (el('export-prestations-count')) el('export-prestations-count').textContent = offlinePrestations.length;
  if (el('export-inventories-count')) el('export-inventories-count').textContent = Object.keys(updatedInventories).length;
}

function exportData() {
  const invCount = Object.keys(updatedInventories).length;
  const total = offlineInvoices.length + offlineExpenses.length + offlineClients.length + offlinePrestations.length + invCount;
  if (total === 0) { toast.warning('Aucune donnée à exporter. Créez des factures, dépenses, clients, prestations ou complétez un inventaire.'); return; }
  
  const exportPayload = {
    invoices: offlineInvoices,
    expenses: offlineExpenses,
    clients: offlineClients,
    prestations: offlinePrestations,
    inventories: Object.values(updatedInventories),
    exported_from_offline: true,
    original_export_date: EXPORTED_AT,
    export_date: new Date().toISOString()
  };
  
  const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'donnees-hors-ligne-' + new Date().toISOString().slice(0, 10) + '.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  toast.success('Fichier téléchargé ! Importez-le dans votre workspace en ligne quand vous serez connecté.');
}

// ==========================================
// UTILS
// ==========================================
function esc(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ==========================================
// INIT
// ==========================================
renderInvoicesList();
renderExpensesList();
renderPaymentsList();
renderPrestationsList();
renderInventoryList();
updateExportStats();
</script>
</body>
</html>`;
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default BusinessOffline;
